import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  Dumbbell, Plus, LogOut, Check, Clock, ChevronRight, UserPlus,
  Play, Send, Camera, MessageCircle, Calendar, TrendingUp, AlertCircle,
  Trash2, Video, Weight, Timer, ChevronDown, X, Menu,
  Home, ClipboardList, BarChart3, Settings, Bell, Image, Upload, Repeat,
  CheckCircle2, Circle, Flame, ArrowRight, Trophy, Star, Award, Zap,
  Edit, Droplet, Minus, Ruler, Scale, Save,
  Activity, Heart, Footprints, Apple, Moon, Bike, FolderOpen,
  Volume2, VolumeX, Pause, SkipForward,
  Route as RouteIcon, Target, Flag, StopCircle, Search, User as UserIcon, Lock, Unlock,
  Waves, Mountain, Snowflake, Sailboat,
  LifeBuoy
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api, soft, API } from "../api/client";
import { wsUrl } from "../lib/wsUrl";
import HydrationWidget from "../components/HydrationWidget";
import HealthCheckWidget from "../components/HealthCheckWidget";
import { GpsService } from "../services/gps.service";
import { HapticService } from "../services/haptic.service";
import { CameraService } from "../services/camera.service";
import { OfflineService } from "../services/offline.service";
import { LocalNotificationService } from "../services/local-notification.service";
import { useHealthSync } from "../hooks/useHealthSync";
import { updateWidgetData } from "../services/widget.service";
import SettingsView from "../components/SettingsView";
import HelpButton from "../components/HelpButton";
import IngredientSearch from "../components/IngredientSearch";
import ChatView from "../components/ChatView";
import WorkoutTimer from "../components/WorkoutTimer";
import { RunMap, MiniRunMap } from "../components/RunMap";
import MiniRing from "../components/client/MiniRing";
import {
  intervalTypeNames, difficultyLabels, activityTypeLabels,
  gpsActivityTypes, formatRunTime, setActivityCatalogue,
} from "../constants/running";
import DietTab from "../components/client/DietTab";
import RunningPlanDetail from "../components/client/RunningPlanDetail";
import RunningPlanEditor from "../components/client/RunningPlanEditor";
import ActiveRunSession from "../components/client/ActiveRunSession";
import RunSessionSummary from "../components/client/RunSessionSummary";
import GuestBanner from "../components/GuestBanner";

const ClientDashboard = () => {
  const { user, token, logout, updateUser, onNotification, notifications: realtimeNotifs, markNotificationsRead } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { latestWeightKg, todaySteps, syncWorkout } = useHealthSync({ authToken: token });
  const [today, setToday] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [habits, setHabits] = useState([]);
  // Seria dla każdego nawyku. Endpoint /habit-logs/streak/{id} istniał i nie
  // miał wywołania, bo cała sekcja nawyków nie była renderowana.
  const [habitStreaks, setHabitStreaks] = useState({});
  const [photoComparison, setPhotoComparison] = useState(null);
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [workoutLog, setWorkoutLog] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("today");
  const [connectOpen, setConnectOpen] = useState(false);
  const [keepPreviousAccess, setKeepPreviousAccess] = useState(false);
  const [myTrainers, setMyTrainers] = useState([]);
  const [myClientCode, setMyClientCode] = useState("");
  const [transferRequests, setTransferRequests] = useState([]);
  const [activityCatalogueVersion, setActivityCatalogueVersion] = useState(0);
  const [trainerCode, setTrainerCode] = useState("");
  const [restTimer, setRestTimer] = useState(null);
  const [restSeconds, setRestSeconds] = useState(0);
  const [morphPosition, setMorphPosition] = useState(50);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [timerParams, setTimerParams] = useState({});
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [techniqueUploadOpen, setTechniqueUploadOpen] = useState(false);
  const [newPhoto, setNewPhoto] = useState({ photo_url: "", photo_type: "front", notes: "" });
  const [newTechniqueVideo, setNewTechniqueVideo] = useState({ video_url: "", exercise_name: "", notes: "" });
  // Gamification & Progress
  const [gamification, setGamification] = useState(null);
  const [progressHistory, setProgressHistory] = useState(null);
  const [expandedLogIndex, setExpandedLogIndex] = useState(null);
  const [customMetrics, setCustomMetrics] = useState([]);
  const [todayMetricLogs, setTodayMetricLogs] = useState([]);
  const [metricLogOpen, setMetricLogOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [metricLogValue, setMetricLogValue] = useState("");
  const [availableExercises, setAvailableExercises] = useState([]);
  const [gifPreview, setGifPreview] = useState(null); // { url, name }
  const [dietGen, setDietGen] = useState(null);
  const [genningDiet, setGenningDiet] = useState(false);
  const [dietKcal, setDietKcal] = useState(2000);
  const [savingDiet, setSavingDiet] = useState(false);
  const [savedDiets, setSavedDiets] = useState([]);
  const [planBuilderOpen, setPlanBuilderOpen] = useState(false);
  const [planExerciseSearch, setPlanExerciseSearch] = useState("");
  // null = tworzenie nowego planu, id = edycja istniejącego. Karta planu na
  // liście nie robiła nic po tapnięciu — nic nie otwierało go do edycji, bo
  // backend nie miał nawet PUT-a dla /workout-plans/{id}.
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planName, setPlanName] = useState("");
  const [planSel, setPlanSel] = useState([]); // selected exercise ids
  const [savingPlan, setSavingPlan] = useState(false);
  const exerciseImg = useMemo(() => {
    const m = {};
    (availableExercises || []).forEach((e) => { if (e.image_url) m[e.id] = e.image_url; });
    return m;
  }, [availableExercises]);
  const [exercisePickerOpen, setExercisePickerOpen] = useState(false);
  const [pendingForms, setPendingForms] = useState([]);
  const [formFillingOpen, setFormFillingOpen] = useState(false);
  const [fillingForm, setFillingForm] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [todayNutrition, setTodayNutrition] = useState({ logs: [], totals: { calories: 0, protein: 0, carbs: 0, fat: 0 }, targets: null, meal_plan: null });
  const [nutritionLogOpen, setNutritionLogOpen] = useState(false);
  const [newNutritionLog, setNewNutritionLog] = useState({ meal_type: "breakfast", items: [{ name: "", calories: 0, protein: 0, carbs: 0, fat: 0, portion: "" }] });
  const [clientSessions, setClientSessions] = useState([]);
  // Rating
  const [ratingOpen, setRatingOpen] = useState(false);
  const [ratingSession, setRatingSession] = useState(null);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  // Booking
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingDate, setBookingDate] = useState("");
  const [bookingSlots, setBookingSlots] = useState([]);
  const [bookingSlotsLoading, setBookingSlotsLoading] = useState(false);
  const [bookingNotes, setBookingNotes] = useState("");
  const [myBookings, setMyBookings] = useState([]);
  const [clientNotifications, setClientNotifications] = useState([]);
  const [clientUnreadCount, setClientUnreadCount] = useState(0);
  const [clientNotifOpen, setClientNotifOpen] = useState(false);
  const [clientFiles, setClientFiles] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [caloriesBurned, setCaloriesBurned] = useState(null);
  const [allWorkoutPlans, setAllWorkoutPlans] = useState([]);
  const [latestMeasurements, setLatestMeasurements] = useState([]);
  // ==================== CARDIO STATE ====================
  const [runningPlans, setRunningPlans] = useState([]);
  const [searchRunningPlans, setSearchRunningPlans] = useState("");
  const [runningSessions, setRunningSessions] = useState([]);
  const [runningStats, setRunningStats] = useState(null);
  const [runningView, setRunningView] = useState("list");
  const [selectedRunningPlan, setSelectedRunningPlan] = useState(null);
  const [activeRunningSession, setActiveRunningSession] = useState(null);
  const [viewingSessionSummary, setViewingSessionSummary] = useState(null);
  const [editingRunningPlan, setEditingRunningPlan] = useState(null);
  const [selectedActivityType, setSelectedActivityType] = useState("running");
  const [manualDistance, setManualDistance] = useState("");
  const [manualIncline, setManualIncline] = useState("");
  const [manualResistance, setManualResistance] = useState("");
  const [manualCadence, setManualCadence] = useState("");
  const [manualPower, setManualPower] = useState("");
  const [newRunningPlan, setNewRunningPlan] = useState({
    name: "", description: "", goal: "", difficulty: "medium",
    intervals: [], tags: [], estimated_distance_km: "", estimated_duration_minutes: "",
    activity_type: "running"
  });
  const [newInterval, setNewInterval] = useState({
    type: "run", duration_seconds: 300, target_pace_min: "", target_pace_max: "", description: ""
  });
  const [currentPace, setCurrentPace] = useState(0);
  const [averagePace, setAveragePace] = useState(0);
  const [totalDistance, setTotalDistance] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentIntervalIndex, setCurrentIntervalIndex] = useState(0);
  const [intervalElapsed, setIntervalElapsed] = useState(0);
  const [isRunningPaused, setIsRunningPaused] = useState(false);
  const [coachMessages, setCoachMessages] = useState([]);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  // AI Coach
  const [aiCoachOpen, setAiCoachOpen] = useState(false);
  const [aiCoachLoading, setAiCoachLoading] = useState(false);
  const [aiCoachForm, setAiCoachForm] = useState({ goal: "general_fitness", difficulty: "medium", duration_minutes: 30, activity_type: "running" });
  const [aiGeneratedPlan, setAiGeneratedPlan] = useState(null);
  const [aiCoachNotes, setAiCoachNotes] = useState("");
  const [aiProgressOpen, setAiProgressOpen] = useState(false);
  const [aiProgressLoading, setAiProgressLoading] = useState(false);
  const [aiProgressText, setAiProgressText] = useState("");
  const [aiRunningInsights, setAiRunningInsights] = useState("");
  const [gpsTrack, setGpsTrack] = useState([]);
  const [sessionGpsTrack, setSessionGpsTrack] = useState([]);
  const [screenLocked, setScreenLocked] = useState(false);
  // Sesja "active" w bazie, do której nie ma już żywego połączenia w tej
  // karcie (cofnięcie/zamknięcie karty w trakcie treningu) — patrz
  // getActiveRunningSession poniżej.
  const [orphanSession, setOrphanSession] = useState(null);

  const timerRef = useRef(null);
  const animationRef = useRef(null);
  const gpsWatchRef = useRef(null);
  const runningWsRef = useRef(null);
  const runningTimerRef = useRef(null);
  const lastGpsRef = useRef(null);
  const gpsBatchRef = useRef([]);
  const batchIntervalRef = useRef(null);

  useEffect(() => {
    fetchData();
    loadSavedDiets();
    return () => {
      clearInterval(timerRef.current);
      cancelAnimationFrame(animationRef.current);
      if (gpsServiceRef.current) gpsServiceRef.current.stopTracking();
      if (runningTimerRef.current) clearInterval(runningTimerRef.current);
      if (batchIntervalRef.current) clearInterval(batchIntervalRef.current);
      if (runningWsRef.current) runningWsRef.current.close();
    };
  }, []);

  // Flush any GPS/finish data queued offline (lost network mid-run, or app
  // closed before it came back) — on mount and whenever connectivity returns.
  useEffect(() => {
    if (!token) return;
    const flush = () => {
      OfflineService.syncGpsBatches(token).catch(() => {});
      OfflineService.syncSessionFinish(token).catch(() => {});
    };
    flush();
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
  }, [token]);

  // Update iOS widget when readiness or gamification changes
  useEffect(() => {
    if (readiness?.score != null || gamification?.workout_streak) {
      updateWidgetData({
        readinessScore: readiness?.score || 0,
        readinessLevel: readiness?.score >= 70 ? 'high' : readiness?.score >= 40 ? 'medium' : 'low',
        nextSessionTime: myBookings?.[0]?.time || null,
        nextSessionTrainer: null,
        streakDays: gamification?.workout_streak || 0,
      });
    }
  }, [readiness, gamification, myBookings]);

  // Check for unrated completed sessions and prompt rating
  useEffect(() => {
    const checkUnratedSessions = async () => {
      try {
        const res = await api("get", "/booking/my?status=past", null, token);
        const pastSessions = res.data || [];
        const completed = pastSessions.filter(s => s.status === 'completed');
        for (const session of completed.slice(0, 3)) { // check last 3
          try {
            const ratingRes = await api("get", `/ratings/session/${session.id}`, null, token);
            if (!ratingRes.data) {
              // Found unrated completed session — prompt
              setRatingSession(session);
              setRatingStars(0);
              setRatingComment("");
              setRatingOpen(true);
              break;
            }
          } catch { /* no rating exists */
            setRatingSession(session);
            setRatingStars(0);
            setRatingComment("");
            setRatingOpen(true);
            break;
          }
        }
      } catch {}
    };
    if (token) {
      // Delay the check so it doesn't block initial load
      const timer = setTimeout(checkUnratedSessions, 3000);
      return () => clearTimeout(timer);
    }
  }, [token]);

  // Sesja treningowa zostawiona "active" w bazie, bo nikt jej nie skończył
  // z tej karty (cofnięcie/reload w trakcie treningu — cały dashboard to
  // jeden ekran bez routingu, więc "wstecz" wychodzi z aplikacji, a stan
  // trwającego treningu żył tylko w pamięci Reacta).
  useEffect(() => {
    if (!token) return;
    api("get", "/running-sessions/active", null, token)
      .then(res => setOrphanSession(res.data || null))
      .catch(() => {});
  }, [token]);

  const resumeOrphanSession = () => {
    const actType = orphanSession.activity_type || "running";
    setActiveRunningSession(orphanSession);
    setSelectedActivityType(actType);
    const started = new Date(orphanSession.started_at).getTime();
    setElapsedSeconds(Math.max(0, Math.floor((Date.now() - started) / 1000)));
    setTotalDistance(0);
    setManualDistance("");
    setRunningView("active_session");
    setOrphanSession(null);
    // Bez ponownego łączenia GPS/WebSocketa — to jest domykanie starej sesji,
    // nie wznowienie żywego trackingu. Trener/klient dopisuje dystans ręcznie
    // i kończy normalnym przyciskiem "Zakończ".
  };

  const abandonOrphanSession = async () => {
    try {
      await api("post", `/running-sessions/${orphanSession.id}/abandon`, null, token);
      toast.success("Porzucony trening zamknięty");
    } catch {
      toast.error("Nie udało się zamknąć treningu");
    } finally {
      setOrphanSession(null);
    }
  };

  // Real-time notification listener
  useEffect(() => {
    if (!onNotification) return;
    return onNotification((notif) => {
      toast.info(notif.title || "Nowe powiadomienie", { description: notif.message });
      setClientUnreadCount(prev => prev + 1);
      // Refresh bookings if it's a session notification
      if (notif.type === "session") fetchData();
    });
  }, [onNotification]);

  useEffect(() => {
    if (activeTab === "trening") {
      fetchProgressData();
    }
    if (activeTab === "cardio") {
      fetchRunningData(selectedActivityType);
    }
    if (activeTab === "progress") {
      fetchProgressData();
      api("get", "/measurements", null, token)
        .then(res => setLatestMeasurements(res.data || []))
        .catch(() => {});
    }
    if (activeTab === "today") {
      api("get", "/health-checks/readiness", null, token)
        .then(res => setReadiness(res.data))
        .catch(() => {});
    }
  }, [activeTab, selectedActivityType]);

  const loadHabitStreaks = async (list) => {
    const entries = await Promise.all((list || []).map(async (h) => {
      try {
        const r = await api("get", `/habit-logs/streak/${h.id}`, null, token);
        return [h.id, r.data];
      } catch {
        return [h.id, null];
      }
    }));
    setHabitStreaks(Object.fromEntries(entries));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [todayRes, snapshotRes, habitsRes, photosRes, gamRes, metricsRes, metricLogsRes, pendingFormsRes, nutritionRes, sessionsRes, clientUnreadRes, clientFilesRes, workoutPlansRes, bookingsRes] = await Promise.all([
        soft(api("get", "/today", null, token), null, "/today"),
        api("get", "/weekly-snapshot", null, token).catch(() => ({ data: null })),
        api("get", "/habit-logs/today", null, token).catch(() => ({ data: [] })),
        api("get", "/progress-photos/comparison", null, token).catch(() => ({ data: null })),
        api("get", "/gamification/stats", null, token).catch(() => ({ data: null })),
        api("get", "/custom-metrics", null, token).catch(() => ({ data: [] })),
        api("get", "/metric-logs/today", null, token).catch(() => ({ data: [] })),
        api("get", "/form-assignments/pending", null, token).catch(() => ({ data: [] })),
        api("get", "/nutrition-logs/today", null, token).catch(() => ({ data: { logs: [], totals: { calories: 0, protein: 0, carbs: 0, fat: 0 }, targets: null, meal_plan: null } })),
        api("get", "/sessions?status=scheduled", null, token).catch(() => ({ data: [] })),
        api("get", "/notifications/unread-count", null, token).catch(() => ({ data: { count: 0 } })),
        api("get", "/shared-files", null, token).catch(() => ({ data: [] })),
        api("get", "/workout-plans", null, token).catch(() => ({ data: [] })),
        api("get", "/booking/my?status=upcoming", null, token).catch(() => ({ data: [] }))
      ]);
      setToday(todayRes.data);
      setSnapshot(snapshotRes.data);
      setHabits(habitsRes.data || []);
      loadHabitStreaks(habitsRes.data || []);
      setPhotoComparison(photosRes.data);
      setGamification(gamRes.data);
      setCustomMetrics(metricsRes.data || []);
      setTodayMetricLogs(metricLogsRes.data || []);
      setPendingForms(pendingFormsRes.data || []);
      setTodayNutrition(nutritionRes.data || { logs: [], totals: { calories: 0, protein: 0, carbs: 0, fat: 0 }, targets: null, meal_plan: null });
      setClientSessions(sessionsRes.data || []);
      setClientUnreadCount(clientUnreadRes.data?.count || 0);
      setClientFiles(clientFilesRes.data || []);
      setAllWorkoutPlans(workoutPlansRes.data || []);
      setMyBookings(bookingsRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressData = async () => {
    try {
      const [historyRes, gamRes] = await Promise.all([
        api("get", "/progress/history", null, token),
        api("get", "/gamification/stats", null, token)
      ]);
      setProgressHistory(historyRes.data);
      setGamification(gamRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  // ==================== BOOKING FUNCTIONS ====================

  const fetchAvailableSlots = async (date) => {
    setBookingSlotsLoading(true);
    try {
      const res = await api("get", `/booking/available-slots?date=${date}`, null, token);
      setBookingSlots(res.data?.slots || []);
    } catch (err) {
      setBookingSlots([]);
    } finally {
      setBookingSlotsLoading(false);
    }
  };

  const requestBooking = async (time, endTime) => {
    try {
      await api("post", "/booking/request", {
        date: bookingDate,
        time: time,
        duration: 60,
        type: "training",
        notes: bookingNotes || null
      }, token);
      toast.success("Prośba o rezerwację wysłana!");
      setBookingOpen(false);
      setBookingDate("");
      setBookingSlots([]);
      setBookingNotes("");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się zarezerwować");
    }
  };

  const cancelMyBooking = async (sessionId) => {
    if (!window.confirm("Anulować rezerwację?")) return;
    try {
      await api("post", `/booking/${sessionId}/cancel`, {}, token);
      toast.success("Rezerwacja anulowana");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się anulować");
    }
  };

  // ==================== RATING FUNCTIONS ====================

  const submitRating = async () => {
    if (!ratingSession || ratingStars === 0) return;
    try {
      await api("post", "/ratings", {
        session_id: ratingSession.id,
        rating: ratingStars,
        comment: ratingComment || null
      }, token);
      toast.success("Dziękujemy za ocenę!");
      setRatingOpen(false);
      setRatingSession(null);
      setRatingStars(0);
      setRatingComment("");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się ocenić");
    }
  };

  // ==================== RUNNING FUNCTIONS ====================

  const fetchRunningData = async (actType) => {
    const at = actType || selectedActivityType;
    try {
      const [plansRes, sessionsRes, statsRes, insightsRes] = await Promise.all([
        api("get", `/running-plans?activity_type=${at}`, null, token).catch(() => ({ data: [] })),
        api("get", `/running-sessions?activity_type=${at}`, null, token).catch(() => ({ data: [] })),
        api("get", "/running-sessions/stats", null, token).catch(() => ({ data: null })),
        api("get", "/ai/running-insights", null, token).catch(() => ({ data: { insights: "" } }))
      ]);
      setRunningPlans(plansRes.data || []);
      setRunningSessions(sessionsRes.data || []);
      setRunningStats(statsRes.data);
      setAiRunningInsights(insightsRes.data?.insights || "");
    } catch (err) {
      console.error("Running data fetch error:", err);
    }
  };

  // ==================== AI COACH FUNCTIONS ====================

  const handleGenerateAiPlan = async () => {
    setAiCoachLoading(true);
    try {
      const res = await api("post", "/ai/generate-plan", aiCoachForm, token);
      setAiGeneratedPlan(res.data.plan);
      setAiCoachNotes(res.data.coaching_notes || "");
      toast.success("Plan AI wygenerowany");
    } catch (err) {
      toast.error("Nie udalo sie wygenerowac planu AI");
    } finally {
      setAiCoachLoading(false);
    }
  };

  const handleAcceptAiPlan = async () => {
    if (!aiGeneratedPlan) return;
    try {
      const planData = {
        name: aiGeneratedPlan.name,
        description: aiGeneratedPlan.description || "",
        goal: aiGeneratedPlan.goal || aiCoachForm.goal,
        difficulty: aiGeneratedPlan.difficulty || aiCoachForm.difficulty,
        estimated_duration_minutes: aiGeneratedPlan.estimated_duration_minutes || aiCoachForm.duration_minutes,
        estimated_distance_km: aiGeneratedPlan.estimated_distance_km || 0,
        intervals: aiGeneratedPlan.intervals || [],
        tags: aiGeneratedPlan.tags || ["ai_generated"],
        type: "ai_generated",
        activity_type: aiGeneratedPlan.activity_type || aiCoachForm.activity_type || selectedActivityType
      };
      await api("post", "/running-plans", planData, token);
      toast.success("Plan dodany do Twoich planow biegowych");
      setAiCoachOpen(false);
      setAiGeneratedPlan(null);
      setAiCoachNotes("");
      fetchRunningData();
    } catch (err) {
      toast.error("Nie udalo sie zapisac planu");
    }
  };

  const handleAiProgress = async () => {
    setAiProgressLoading(true);
    try {
      const res = await api("post", "/ai/analyze-progress", null, token);
      setAiProgressText(res.data.analysis || "");
    } catch (err) {
      toast.error("Nie udalo sie przeanalizowac postepoow");
    } finally {
      setAiProgressLoading(false);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const calculatePace = (speedMs) => {
    if (!speedMs || speedMs <= 0) return 0;
    return 1000 / (speedMs * 60);
  };

  const gpsServiceRef = useRef(null);

  const startGpsTracking = async () => {
    const svc = new GpsService({
      onPoint: (point) => {
        const legacyPoint = {
          timestamp: new Date(point.timestamp).toISOString(),
          latitude: point.lat,
          longitude: point.lng,
          accuracy: point.accuracy,
          altitude: point.altitude,
          speed: point.speed,
        };
        if (lastGpsRef.current && point.accuracy < 30) {
          const dist = calculateDistance(
            lastGpsRef.current.latitude, lastGpsRef.current.longitude,
            point.lat, point.lng
          );
          if (dist > 0.001 && dist < 0.5) {
            setTotalDistance(prev => {
              const newDist = prev + dist;
              if (Math.floor(newDist) > Math.floor(prev)) HapticService.kmSplit();
              return newDist;
            });
          }
        }
        if (point.speed !== null && point.speed > 0) {
          setCurrentPace(calculatePace(point.speed));
        }
        lastGpsRef.current = legacyPoint;
        gpsBatchRef.current.push(legacyPoint);
        if (point.accuracy < 50) {
          setGpsTrack(prev => [...prev, legacyPoint]);
        }
      },
      onError: (err) => {
        console.error("GPS error:", err);
        toast.error(err);
      },
      pushIntervalMs: 30000,
    });
    gpsServiceRef.current = svc;

    const perm = await svc.requestPermission();
    if (!perm) return;

    // Pass the running WebSocket so GpsService pushes gps_track automatically
    const ws = runningWsRef.current;
    await svc.startTracking(ws);
    gpsWatchRef.current = 1; // truthy marker for compatibility
  };

  const stopGpsTracking = () => {
    if (gpsServiceRef.current) {
      gpsServiceRef.current.stopTracking();
      gpsServiceRef.current = null;
    }
    gpsWatchRef.current = null;
  };

  const speak = (text) => {
    if (!voiceEnabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "pl-PL";
    utterance.rate = 1.0;
    utterance.volume = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const polishVoice = voices.find(v => v.lang.startsWith("pl"));
    if (polishVoice) utterance.voice = polishVoice;
    window.speechSynthesis.speak(utterance);
  };

  const connectRunningWebSocket = (sessionId) => {
    runningWsRef.current = new WebSocket(wsUrl(`/ws/running/${sessionId}`, token));
    runningWsRef.current.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.error("WebSocket message parse error:", e);
        return;
      }
      if (data.type === "coaching") {
        setCoachMessages(prev => [...prev, data]);
        speak(data.message);
      }
    };
    runningWsRef.current.onerror = (err) => console.error("Running WS error:", err);
  };

  const sendGpsToWebSocket = () => {
    if (!runningWsRef.current || runningWsRef.current.readyState !== WebSocket.OPEN) return;
    runningWsRef.current.send(JSON.stringify({
      type: "gps_update",
      current_pace: currentPace,
      average_pace: averagePace,
      distance_km: totalDistance,
      elapsed_seconds: elapsedSeconds,
      current_interval_index: currentIntervalIndex
    }));
  };

  const startRunningSession = async (plan = null) => {
    try {
      const actType = plan?.activity_type || selectedActivityType;
      const useGps = gpsActivityTypes.includes(actType);
      const res = await api("post", "/running-sessions", {
        plan_id: plan?.id || null,
        goal: plan?.goal || null,
        activity_type: actType
      }, token);
      const session = res.data;
      setActiveRunningSession(session);
      setRunningView("active_session");
      setTotalDistance(0);
      setElapsedSeconds(0);
      setCurrentIntervalIndex(0);
      setIntervalElapsed(0);
      setCurrentPace(0);
      setAveragePace(0);
      setCoachMessages([]);
      setIsRunningPaused(false);
      setManualDistance("");
      setManualIncline("");
      setManualResistance("");
      setManualCadence("");
      setManualPower("");
      lastGpsRef.current = null;
      gpsBatchRef.current = [];
      setGpsTrack([]);
      if (useGps) {
        startGpsTracking();
      }
      connectRunningWebSocket(session.id);
      runningTimerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
        setIntervalElapsed(prev => prev + 1);
      }, 1000);
      if (useGps) {
        batchIntervalRef.current = setInterval(async () => {
          OfflineService.syncGpsBatches(token).catch(() => {});
          if (gpsBatchRef.current.length > 0) {
            const points = [...gpsBatchRef.current];
            gpsBatchRef.current = [];
            try {
              await api("post", `/running-sessions/${session.id}/track`, { points, elapsed_seconds: 0 }, token);
            } catch (e) {
              console.error("GPS tracking error:", e);
              await OfflineService.queueGpsBatch(session.id, points);
            }
          }
          sendGpsToWebSocket();
        }, 10000);
      }
      const actLabel = activityTypeLabels[actType] || "Trening";
      speak(`${actLabel} rozpoczęty. Powodzenia!`);
      toast.success(`${actLabel} rozpoczęty!`);
    } catch (err) {
      toast.error("Nie udało się rozpocząć treningu");
    }
  };

  const pauseRunningSession = () => {
    setIsRunningPaused(true);
    clearInterval(runningTimerRef.current);
    if (runningWsRef.current?.readyState === WebSocket.OPEN) {
      runningWsRef.current.send(JSON.stringify({ type: "pause" }));
    }
    speak("Pauza.");
  };

  const resumeRunningSession = () => {
    setIsRunningPaused(false);
    runningTimerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
      setIntervalElapsed(prev => prev + 1);
    }, 1000);
    if (runningWsRef.current?.readyState === WebSocket.OPEN) {
      runningWsRef.current.send(JSON.stringify({ type: "resume" }));
    }
    speak("Wznowiono.");
  };

  const finishRunningSession = async () => {
    const actType = activeRunningSession?.activity_type || selectedActivityType;
    const useGps = gpsActivityTypes.includes(actType);
    // Bez GPS dystans jest wyłącznie z pola ręcznego. Z GPS też bywa zerowy —
    // brak uprawnienia do lokalizacji, słaby sygnał (w budynku) albo zbyt
    // krótki/wolny odcinek, żeby cokolwiek naliczyć — a manualDistance jest
    // wtedy jedynym zapasowym polem, bo nic innego go nie proponuje. W obu
    // przypadkach puste pole kończyło trening z dystansem i kaloriami = 0 —
    // zapisane, ale wyglądające tak, jakby nic się nie nagrało.
    if (!(useGps ? totalDistance > 0 : false) && !parseFloat(manualDistance)) {
      toast.error(
        useGps
          ? "GPS nie namierzył dystansu — wpisz go ręcznie, żeby zakończyć trening"
          : "Wpisz przebyty dystans, żeby zakończyć trening"
      );
      return;
    }
    clearInterval(runningTimerRef.current);
    clearInterval(batchIntervalRef.current);
    if (useGps) stopGpsTracking();
    if (runningWsRef.current) runningWsRef.current.close();
    if (useGps && gpsBatchRef.current.length > 0) {
      const points = [...gpsBatchRef.current];
      try {
        await api("post", `/running-sessions/${activeRunningSession.id}/track`, {
          points, elapsed_seconds: elapsedSeconds
        }, token);
      } catch (e) {
        console.error("GPS tracking error:", e);
        await OfflineService.queueGpsBatch(activeRunningSession.id, points);
      }
      gpsBatchRef.current = [];
    }
    // Non-GPS: dystans zawsze z pola ręcznego. GPS: normalnie z trackingu,
    // ale gdy ten nie namierzył nic (patrz walidacja wyżej), ratuje to samo
    // pole ręczne, które wtedy jest jedyną drogą do zakończenia treningu.
    const finalDistance = useGps ? (totalDistance > 0 ? totalDistance : (parseFloat(manualDistance) || 0))
                                  : (parseFloat(manualDistance) || totalDistance);
    const avgPace = finalDistance > 0 ? (elapsedSeconds / 60) / finalDistance : 0;
    const finishData = {
      total_distance_km: Math.round(finalDistance * 100) / 100,
      total_duration_seconds: elapsedSeconds,
      average_pace: Math.round(avgPace * 10) / 10,
      calories_burned: Math.round(finalDistance * 70),
      feeling: null,
      notes: null
    };
    // Activity-specific metrics
    if (actType === "treadmill" && manualIncline) finishData.incline_percent = parseFloat(manualIncline);
    if (actType === "ergometer") {
      if (manualResistance) finishData.resistance_level = parseInt(manualResistance);
      if (manualCadence) finishData.average_cadence_rpm = parseFloat(manualCadence);
      if (manualPower) finishData.average_power_watts = parseFloat(manualPower);
    }
    try {
      const res = await api("post", `/running-sessions/${activeRunningSession.id}/finish`, finishData, token);
      speak("Trening zakończony. Świetna robota!");
      setViewingSessionSummary(res.data);
      setAiAnalysis(res.data.ai_analysis || null);
      setSessionGpsTrack(gpsTrack);
      setRunningView("session_summary");
      setActiveRunningSession(null);
      setScreenLocked(false);
    } catch (err) {
      await OfflineService.queueSessionFinish(activeRunningSession.id, finishData);
      toast.error("Brak sieci — trening zostanie zapisany automatycznie, gdy sieć wróci");
    }
  };

  const downloadSessionGpx = async (session) => {
    try {
      const res = await fetch(`${API}/running-sessions/${session.id}/gpx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.error("Brak danych GPS do eksportu");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `duofit_trening_${(session.started_at || "").slice(0, 10)}.gpx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Nie udało się pobrać pliku GPX");
    }
  };

  const requestAiAnalysis = async (sessionId) => {
    setAiAnalysisLoading(true);
    try {
      const res = await api("post", `/running-sessions/${sessionId}/ai-analyze`, null, token);
      setAiAnalysis(res.data.analysis);
    } catch (err) {
      toast.error("Analiza AI niedostępna");
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  // Interval tracking
  useEffect(() => {
    if (!activeRunningSession || isRunningPaused) return;
    const plan = activeRunningSession.plan_snapshot;
    if (!plan || !plan.intervals || plan.intervals.length === 0) return;
    const intervals = plan.intervals;
    if (currentIntervalIndex >= intervals.length) return;
    const currentInterval = intervals[currentIntervalIndex];
    if (intervalElapsed >= currentInterval.duration_seconds) {
      const nextIndex = currentIntervalIndex + 1;
      if (nextIndex < intervals.length) {
        setCurrentIntervalIndex(nextIndex);
        setIntervalElapsed(0);
      } else {
        speak("Wszystkie interwały ukończone! Możesz zakończyć trening.");
      }
    }
  }, [intervalElapsed, currentIntervalIndex, activeRunningSession, isRunningPaused]);

  useEffect(() => {
    if (elapsedSeconds > 0 && totalDistance > 0) {
      setAveragePace((elapsedSeconds / 60) / totalDistance);
    }
  }, [elapsedSeconds, totalDistance]);

  // Voice preload
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    }
  }, []);

  const addInterval = () => {
    const interval = {
      order: newRunningPlan.intervals.length,
      type: newInterval.type,
      duration_seconds: parseInt(newInterval.duration_seconds) || 300,
      target_pace_min: newInterval.target_pace_min ? parseFloat(newInterval.target_pace_min) : null,
      target_pace_max: newInterval.target_pace_max ? parseFloat(newInterval.target_pace_max) : null,
      description: newInterval.description || ""
    };
    setNewRunningPlan(prev => ({ ...prev, intervals: [...prev.intervals, interval] }));
    setNewInterval({ type: "run", duration_seconds: 300, target_pace_min: "", target_pace_max: "", description: "" });
  };

  const removeInterval = (index) => {
    setNewRunningPlan(prev => ({
      ...prev,
      intervals: prev.intervals.filter((_, i) => i !== index).map((int, i) => ({ ...int, order: i }))
    }));
  };

  const saveRunningPlan = async () => {
    if (!newRunningPlan.name.trim()) { toast.error("Podaj nazwę planu"); return; }
    if (newRunningPlan.intervals.length === 0) { toast.error("Dodaj przynajmniej jeden interwał"); return; }
    const payload = {
      ...newRunningPlan,
      estimated_distance_km: newRunningPlan.estimated_distance_km ? parseFloat(newRunningPlan.estimated_distance_km) : null,
      estimated_duration_minutes: newRunningPlan.estimated_duration_minutes ? parseInt(newRunningPlan.estimated_duration_minutes) : null,
      type: "custom"
    };
    try {
      if (editingRunningPlan) {
        await api("put", `/running-plans/${editingRunningPlan.id}`, payload, token);
        toast.success("Plan zaktualizowany");
      } else {
        await api("post", "/running-plans", payload, token);
        toast.success("Plan utworzony");
      }
      setRunningView("list");
      setEditingRunningPlan(null);
      setNewRunningPlan({ name: "", description: "", goal: "", difficulty: "medium", intervals: [], tags: [], estimated_distance_km: "", estimated_duration_minutes: "" });
      fetchRunningData();
    } catch (err) {
      toast.error("Nie udało się zapisać planu");
    }
  };

  const deleteRunningPlan = async (planId) => {
    try {
      await api("delete", `/running-plans/${planId}`, null, token);
      toast.success("Plan usunięty");
      fetchRunningData();
    } catch (err) {
      toast.error("Nie udało się usunąć planu");
    }
  };


  // Ikony dobierane po rodzinie aktywności, z sensownym domyślnym — katalog
  // przychodzi z serwera i może urosnąć bez ruszania tego pliku.
  const activityTypeIcons = {
    running: Footprints, trail_running: Mountain, walking: Footprints, hiking: Mountain,
    nordic_walking: Footprints, cross_country_skiing: Snowflake,
    cycling: Bike, mountain_biking: Bike, indoor_cycling: Bike, inline_skating: Bike,
    kayaking: Sailboat, swimming: Waves, open_water_swimming: Waves,
    treadmill: Timer, ergometer: Timer, elliptical: Timer, stair_climber: Timer,
  };
  const freeSessionLabel = (key) => `Wolny trening — ${(activityTypeLabels[key] || "cardio").toLowerCase()}`;

  const openExerciseTimer = (exercise) => {
    setTimerParams({
      exerciseName: exercise.exercise_name,
      initialWork: exercise.tabata_work || 20,
      initialRest: exercise.tabata_rest || 10,
      initialRounds: exercise.tabata_rounds || 8
    });
    setShowTimer(true);
  };

  const toggleHabit = async (habit) => {
    try {
      await api("post", "/habit-logs", {
        habit_id: habit.id,
        completed: !habit.completed
      }, token);
      setHabits(prev => prev.map(h => 
        h.id === habit.id ? { ...h, completed: !h.completed } : h
      ));
      toast.success(habit.completed ? "Anulowano" : "Nawyk wykonany!");
      loadHabitStreaks(habits);
    } catch (err) {
      toast.error("Nie udało się zaktualizować");
    }
  };

  const clientMetricIconMap = { Activity, Heart, Moon, Footprints, Flame, Star, Zap, TrendingUp, Droplet };

  const logMetricValue = async () => {
    if (!selectedMetric || !metricLogValue) return;
    try {
      await api("post", "/metric-logs", {
        metric_id: selectedMetric.id,
        value: parseFloat(metricLogValue)
      }, token);
      toast.success(`${selectedMetric.name}: ${metricLogValue} ${selectedMetric.unit}`);
      setMetricLogOpen(false);
      setSelectedMetric(null);
      setMetricLogValue("");
      // Refresh today's logs
      const logsRes = await api("get", "/metric-logs/today", null, token);
      setTodayMetricLogs(logsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zapisać");
    }
  };

  const submitFormResponse = async () => {
    if (!fillingForm) return;
    try {
      await api("post", "/form-responses", {
        assignment_id: fillingForm.id,
        responses: formValues
      }, token);
      toast.success("Formularz wysłany!");
      setFormFillingOpen(false);
      setFillingForm(null);
      setFormValues({});
      fetchData();
    } catch (err) {
      toast.error("Nie udało się wysłać");
    }
  };

  const openFormFilling = (assignment) => {
    setFillingForm(assignment);
    setFormValues({});
    setFormFillingOpen(true);
  };


  const openClientNotifications = async () => {
    try {
      const res = await api("get", "/notifications", null, token);
      // Merge DB notifications with real-time ones (deduplicate by id)
      const dbNotifs = res.data || [];
      const allIds = new Set(dbNotifs.map(n => n.id));
      const merged = [...dbNotifs];
      for (const rn of realtimeNotifs) {
        if (!allIds.has(rn.id)) merged.unshift(rn);
      }
      setClientNotifications(merged);
      setClientNotifOpen(true);
      await api("put", "/notifications/mark-read", null, token);
      setClientUnreadCount(0);
      if (markNotificationsRead) markNotificationsRead();
    } catch (err) {}
  };

  const logNutrition = async () => {
    try {
      const items = newNutritionLog.items.filter(i => i.name.trim());
      if (items.length === 0) return;
      await api("post", "/nutrition-logs", {
        meal_type: newNutritionLog.meal_type,
        items
      }, token);
      toast.success("Posiłek zapisany");
      setNutritionLogOpen(false);
      setNewNutritionLog({ meal_type: "breakfast", items: [{ name: "", calories: 0, protein: 0, carbs: 0, fat: 0, portion: "" }] });
      const res = await api("get", "/nutrition-logs/today", null, token);
      setTodayNutrition(res.data);
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const startMorphAnimation = () => {
    if (isAnimating) {
      setIsAnimating(false);
      cancelAnimationFrame(animationRef.current);
      return;
    }
    
    setIsAnimating(true);
    let position = 0;
    let direction = 1;
    
    const animate = () => {
      position += direction * 0.8;
      if (position >= 100) direction = -1;
      if (position <= 0) direction = 1;
      setMorphPosition(position);
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();
  };

  const startWorkout = async (plan) => {
    try {
      const [res, exRes] = await Promise.all([
        api("get", `/workout-plans/${plan.id}`, null, token),
        api("get", "/exercises", null, token).catch(() => ({ data: [] }))
      ]);
      setActiveWorkout(res.data);
      setAvailableExercises(exRes.data || []);
      const initialLog = {};
      res.data?.exercises?.forEach(ex => {
        initialLog[ex.exercise_id] = ex.sets.map((s, i) => {
          const lastSet = ex.last_logged_sets?.[i];
          return {
            set_number: s.set_number,
            actual_reps: lastSet?.actual_reps || s.target_reps || 10,
            actual_weight: lastSet?.actual_weight || s.target_weight || "",
            completed: false
          };
        });
      });
      setWorkoutLog(initialLog);
    } catch (err) {
      toast.error("Nie udało się załadować treningu");
    }
  };

  const generateMyDiet = async () => {
    setGenningDiet(true);
    try {
      const r = await api("post", "/diet/generate", { target_calories: parseInt(dietKcal) || 2000, days: 1, diet_type: "zbilansowana" }, token);
      setDietGen(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wygenerować diety");
    } finally {
      setGenningDiet(false);
    }
  };

  const loadSavedDiets = async () => {
    try { const r = await api("get", "/diet/plans", null, token); setSavedDiets(r.data || []); } catch { /* ignore */ }
  };

  const saveMyDiet = async () => {
    if (!dietGen?.days?.length) return;
    setSavingDiet(true);
    try {
      await api("post", "/diet/plans", {
        diet_type: dietGen.diet_type || "zbilansowana",
        target_calories: dietGen.target_calories || parseInt(dietKcal) || 2000,
        days: dietGen.days, shopping_list: dietGen.shopping_list || [],
      }, token);
      toast.success("Dieta zapisana");
      loadSavedDiets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zapisać diety");
    } finally {
      setSavingDiet(false);
    }
  };

  const openPlanBuilder = async (existingPlan = null) => {
    setPlanName(existingPlan?.name || "");
    setPlanExerciseSearch("");
    // planSel niesie teraz serie/powtórzenia/ciężar per ćwiczenie — wcześniej
    // był to sam array id-ków i cały plan zawsze zapisywał się na sztywno
    // jako 3×10, bez możliwości zmiany.
    setPlanSel(existingPlan
      ? existingPlan.exercises.map((e) => ({
          exercise_id: e.exercise_id,
          name: e.exercise_name || availableExercises.find(x => x.id === e.exercise_id)?.name || "?",
          sets: e.sets?.length || 3,
          reps: e.sets?.[0]?.target_reps || 10,
          weight: e.sets?.[0]?.target_weight || 0,
        }))
      : []);
    setEditingPlanId(existingPlan?.id || null);
    if (availableExercises.length === 0) {
      try { const r = await api("get", "/exercises", null, token); setAvailableExercises(r.data || []); } catch { /* ignore */ }
    }
    setPlanBuilderOpen(true);
  };

  const addExerciseToPlan = (ex) => {
    if (planSel.some((s) => s.exercise_id === ex.id)) return;
    setPlanSel((prev) => [...prev, { exercise_id: ex.id, name: ex.name, sets: 3, reps: 10, weight: 0 }]);
  };

  const removeExerciseFromPlan = (exerciseId) => {
    setPlanSel((prev) => prev.filter((s) => s.exercise_id !== exerciseId));
  };

  const updatePlanExercise = (exerciseId, field, value) => {
    setPlanSel((prev) => prev.map((s) => s.exercise_id === exerciseId ? { ...s, [field]: value } : s));
  };

  const saveMyPlan = async () => {
    if (!planName.trim()) { toast.error("Podaj nazwę planu"); return; }
    if (planSel.length === 0) { toast.error("Wybierz przynajmniej jedno ćwiczenie"); return; }
    setSavingPlan(true);
    try {
      const exercises = planSel.map((s, i) => ({
        exercise_id: s.exercise_id, order: i,
        sets: Array.from({ length: Math.max(1, parseInt(s.sets) || 1) }, (_, n) => ({
          set_number: n + 1,
          target_reps: Math.max(1, parseInt(s.reps) || 1),
          target_weight: parseFloat(s.weight) || 0,
          rest_seconds: 90,
        })),
        notes: null,
      }));
      if (editingPlanId) {
        await api("put", `/workout-plans/${editingPlanId}`, { name: planName.trim(), exercises }, token);
        toast.success("Plan zapisany");
      } else {
        await api("post", "/workout-plans", { name: planName.trim(), exercises }, token);
        toast.success("Plan utworzony");
      }
      setPlanBuilderOpen(false);
      setEditingPlanId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zapisać planu");
    } finally {
      setSavingPlan(false);
    }
  };

  // ONE-TAP: Complete set with current values instantly
  const oneTapComplete = (exerciseId, setIndex) => {
    // Green flash animation on the set row
    const rows = document.querySelectorAll(`.set-row`);
    const allSets = Object.values(workoutLog).flat();
    let globalIdx = 0;
    for (const eid of Object.keys(workoutLog)) {
      for (let si = 0; si < (workoutLog[eid]?.length || 0); si++) {
        if (eid === exerciseId && si === setIndex) {
          rows[globalIdx]?.classList.add('flash-complete');
          setTimeout(() => rows[globalIdx]?.classList.remove('flash-complete'), 600);
        }
        globalIdx++;
      }
    }
    setWorkoutLog(prev => {
      const updated = { ...prev };
      updated[exerciseId] = [...updated[exerciseId]];
      updated[exerciseId][setIndex] = {
        ...updated[exerciseId][setIndex],
        completed: true
      };
      return updated;
    });
    toast.success("Seria zapisana!");
    HapticService.setComplete();
    startRestTimer(90);
  };

  const toggleSet = (exerciseId, setIndex) => {
    setWorkoutLog(prev => {
      const updated = { ...prev };
      updated[exerciseId] = [...updated[exerciseId]];
      updated[exerciseId][setIndex] = {
        ...updated[exerciseId][setIndex],
        completed: !updated[exerciseId][setIndex].completed
      };
      return updated;
    });
    if (!workoutLog[exerciseId][setIndex].completed) {
      startRestTimer(90);
    }
  };

  const updateSetValue = (exerciseId, setIndex, field, value) => {
    setWorkoutLog(prev => {
      const updated = { ...prev };
      updated[exerciseId] = [...updated[exerciseId]];
      updated[exerciseId][setIndex] = {
        ...updated[exerciseId][setIndex],
        [field]: value
      };
      return updated;
    });
  };

  const startRestTimer = (seconds) => {
    clearInterval(timerRef.current);
    setRestSeconds(seconds);
    setRestTimer(true);
    LocalNotificationService.scheduleRestEnd(seconds);
    timerRef.current = setInterval(() => {
      setRestSeconds(prev => {
        if (prev > 1 && prev <= 4) HapticService.restTimerTick();
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setRestTimer(false);
          HapticService.restTimerEnd();
          LocalNotificationService.cancelRestTimer();
          // Play beep
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 660;
            gain.gain.setValueAtTime(0.3, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
          } catch (e) {}
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const addExerciseToWorkout = (exercise) => {
    if (activeWorkout?.exercises?.find(e => e.exercise_id === exercise.id)) return;
    const newEx = {
      exercise_id: exercise.id,
      exercise_name: exercise.name,
      exercise_category: exercise.category,
      video_url: exercise.video_url,
      tabata_work: exercise.tabata_work,
      tabata_rest: exercise.tabata_rest,
      tabata_rounds: exercise.tabata_rounds,
      order: (activeWorkout?.exercises?.length || 0) + 1,
      sets: [{ set_number: 1, target_reps: 10, target_weight: 0, rest_seconds: 90 }]
    };
    setActiveWorkout(prev => ({ ...prev, exercises: [...prev.exercises, newEx] }));
    setWorkoutLog(prev => ({
      ...prev,
      [exercise.id]: [{ set_number: 1, actual_reps: 10, actual_weight: "", completed: false }]
    }));
    setExercisePickerOpen(false);
    toast.success(`Dodano: ${exercise.name}`);
  };

  const removeExerciseFromWorkout = (exerciseId) => {
    if ((activeWorkout?.exercises?.length || 0) <= 1) {
      toast.error("Trening musi mieć minimum 1 ćwiczenie");
      return;
    }
    setActiveWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.filter(e => e.exercise_id !== exerciseId)
    }));
    setWorkoutLog(prev => {
      const updated = { ...prev };
      delete updated[exerciseId];
      return updated;
    });
  };

  const addSetToExercise = (exerciseId) => {
    const currentSets = workoutLog[exerciseId] || [];
    const lastSet = currentSets[currentSets.length - 1] || { actual_reps: 10, actual_weight: "" };
    const newSetNum = currentSets.length + 1;
    setWorkoutLog(prev => ({
      ...prev,
      [exerciseId]: [...prev[exerciseId], {
        set_number: newSetNum,
        actual_reps: lastSet.actual_reps,
        actual_weight: lastSet.actual_weight,
        completed: false
      }]
    }));
  };

  const removeSetFromExercise = (exerciseId, setIndex) => {
    const currentSets = workoutLog[exerciseId] || [];
    if (currentSets.length <= 1) return;
    setWorkoutLog(prev => ({
      ...prev,
      [exerciseId]: prev[exerciseId]
        .filter((_, i) => i !== setIndex)
        .map((s, i) => ({ ...s, set_number: i + 1 }))
    }));
  };

  const finishWorkout = async () => {
    try {
      const exercises = (activeWorkout?.exercises || []).map(ex => ({
        exercise_id: ex.exercise_id,
        sets: (workoutLog[ex.exercise_id] || []).map(s => ({
          ...s,
          actual_reps: parseInt(s.actual_reps) || null,
          actual_weight: parseFloat(s.actual_weight) || null
        }))
      }));
      
      await api("post", "/workout-logs", {
        workout_plan_id: activeWorkout.id,
        exercises
      }, token);
      
      toast.success("Trening zapisany!");
      HapticService.workoutComplete();
      // Sync to HealthKit / Health Connect
      syncWorkout({
        activityType: 'strength',
        startDate: new Date(Date.now() - 60 * 60 * 1000), // approximate
        endDate: new Date(),
      });
      // Update iOS widget
      updateWidgetData({
        readinessScore: readiness?.score || 0,
        readinessLevel: readiness?.score >= 70 ? 'high' : readiness?.score >= 40 ? 'medium' : 'low',
        nextSessionTime: myBookings[0]?.time || null,
        nextSessionTrainer: null,
        streakDays: gamification?.workout_streak || 0,
      });
      setActiveWorkout(null);
      setWorkoutLog({});
      clearInterval(timerRef.current);
      setRestTimer(false);
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać treningu");
    }
  };

  // Katalog aktywności i lista trenerów z dostępem — jedno i drugie z serwera.
  const loadAccessAndActivities = async () => {
    try {
      const res = await api("get", "/activity-types", null, token);
      setActivityCatalogue(res.data?.activity_types);
      setActivityCatalogueVersion((v) => v + 1);
    } catch { /* zostają wartości awaryjne z constants/running.js */ }
    try {
      const res = await api("get", "/client/trainers", null, token);
      setMyTrainers(res.data?.trainers || []);
    } catch { setMyTrainers([]); }
    try {
      const res = await api("get", "/client/my-code", null, token);
      setMyClientCode(res.data?.client_code || "");
    } catch { setMyClientCode(""); }
    try {
      const res = await api("get", "/client/transfer-requests", null, token);
      setTransferRequests(res.data?.requests || []);
    } catch { setTransferRequests([]); }
  };

  // Zmiana opiekuna w klubie nie dzieje się ponad głową podopiecznego:
  // trener prosi, decyduje ten, o czyje dane chodzi.
  const resolveTransfer = async (request, accept) => {
    try {
      if (accept) {
        const keep = window.confirm(
          `Zgadzasz się, żeby prowadził Cię ${request.to_trainer_name}.\n\n` +
          `OK — ${request.from_trainer_name} zachowa wgląd w Twoje postępy.\n` +
          `Anuluj — ${request.from_trainer_name} straci dostęp do Twoich danych.`);
        await api("post", `/client/transfer-requests/${request.id}/accept`,
          { keep_previous_access: keep }, token);
        toast.success("Zmieniono trenera");
      } else {
        await api("post", `/client/transfer-requests/${request.id}/reject`, null, token);
        toast.success("Prośba odrzucona");
      }
      await loadAccessAndActivities();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się");
    }
  };

  // Kod działa w drugą stronę niż kod trenera: podopieczny pokazuje go trenerowi,
  // a ten podpina go sobie sam. Wcześniej połączenie dało się zawiązać wyłącznie
  // przez wpisanie kodu trenera przez podopiecznego.
  const regenerateMyCode = async () => {
    if (!window.confirm("Wygenerować nowy kod? Stary przestanie działać. Trenerzy, którzy już mają dostęp, zachowają go.")) return;
    try {
      const res = await api("post", "/client/my-code/regenerate", null, token);
      setMyClientCode(res.data?.client_code || "");
      toast.success("Nowy kod wygenerowany");
    } catch { toast.error("Nie udało się"); }
  };

  useEffect(() => { loadAccessAndActivities(); }, [token]);

  const connectTrainer = async () => {
    try {
      const res = await api("post", `/client/connect/${trainerCode}`,
        { keep_previous_access: keepPreviousAccess }, token);
      toast.success(res.data?.previous_access_revoked
        ? "Połączono. Poprzedni trener stracił dostęp do Twoich danych."
        : "Połączono z trenerem");
      setConnectOpen(false);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nieprawidłowy kod");
    }
  };

  // Dane zostają u klienta — odebranie dostępu nie kasuje historii treningów.
  const revokeTrainerAccess = async (trainer) => {
    if (!window.confirm(`Odebrać dostęp trenerowi ${trainer.name}? Twoje dane zostają u Ciebie.`)) return;
    try {
      await api("delete", `/client/trainers/${trainer.id}`, null, token);
      toast.success("Dostęp odebrany");
      await loadAccessAndActivities();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się");
    }
  };

  const uploadProgressPhoto = async () => {
    if (!newPhoto.photo_url && !newPhoto._capturedPhoto) {
      toast.error("Zrób zdjęcie lub podaj URL");
      return;
    }
    try {
      const form = new FormData();
      if (newPhoto._capturedPhoto) {
        // Captured/picked photo — send the real file
        const blob = await (await fetch(newPhoto._capturedPhoto.dataUrl)).blob();
        const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
        form.append("file", blob, `progress-${newPhoto.photo_type}-${Date.now()}.${ext}`);
      } else {
        form.append("photo_url", newPhoto.photo_url);
      }
      form.append("photo_type", newPhoto.photo_type);
      if (newPhoto.notes) form.append("notes", newPhoto.notes);
      await api("post", "/progress-photos/upload", form, token);
      toast.success("Zdjęcie dodane!");
      setPhotoUploadOpen(false);
      setNewPhoto({ photo_url: "", photo_type: "front", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się dodać zdjęcia");
    }
  };

  const uploadTechniqueVideo = async () => {
    if ((!newTechniqueVideo.video_url && !newTechniqueVideo._file) || !newTechniqueVideo.exercise_name) {
      toast.error("Wybierz plik lub podaj URL oraz nazwę ćwiczenia");
      return;
    }
    try {
      const form = new FormData();
      if (newTechniqueVideo._file) {
        form.append("file", newTechniqueVideo._file);
      } else {
        form.append("video_url", newTechniqueVideo.video_url);
      }
      form.append("exercise_name", newTechniqueVideo.exercise_name);
      if (newTechniqueVideo.notes) form.append("notes", newTechniqueVideo.notes);
      await api("post", "/technique-videos/upload", form, token);
      toast.success("Video wysłane do oceny!");
      setTechniqueUploadOpen(false);
      setNewTechniqueVideo({ video_url: "", exercise_name: "", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wysłać video");
    }
  };

  if (loading) return <div className="loading"><Dumbbell className="spin" size={32} /></div>;

  // Active Workout View
  if (activeWorkout) {
    return (
      <div className="app-container mode-focus">
        {showTimer && <WorkoutTimer onClose={() => setShowTimer(false)} {...timerParams} />}
        
        <header className="workout-header">
          <Button variant="ghost" size="sm" onClick={() => setActiveWorkout(null)}>
            <X size={20} />
          </Button>
          <h1>{activeWorkout.name}</h1>
          <div className="workout-header-actions">
            <Button variant="outline" size="sm" onClick={() => setExercisePickerOpen(true)} data-testid="add-exercise-btn">
              <Plus size={16} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setTimerParams({}); setShowTimer(true); }} data-testid="timer-btn">
              <Timer size={16} />
            </Button>
            <Button onClick={finishWorkout} className="finish-btn">Zakończ</Button>
          </div>
        </header>

        {restTimer && (() => {
          const totalRest = 90;
          const pct = restSeconds / totalRest;
          const restCirc = 2 * Math.PI * 70;
          const restOff = restCirc * (1 - pct);
          return (
          <div className="rest-timer-overlay">
            <span className="rest-label">PRZERWA</span>
            <div className="rest-ring-container">
              <svg width="180" height="180" viewBox="0 0 180 180">
                <circle cx="90" cy="90" r="70" fill="none" stroke="rgba(206,255,0,0.1)" strokeWidth="6" />
                <circle cx="90" cy="90" r="70" fill="none" stroke="#CEFF00" strokeWidth="6"
                  strokeLinecap="round" strokeDasharray={restCirc} strokeDashoffset={restOff}
                  transform="rotate(-90 90 90)" className="rest-ring-arc" />
              </svg>
              <span className="rest-countdown">{Math.floor(restSeconds / 60)}:{(restSeconds % 60).toString().padStart(2, '0')}</span>
            </div>
            <Button variant="ghost" size="sm" className="rest-skip-btn" onClick={() => { clearInterval(timerRef.current); setRestTimer(false); }}>
              Pomin
            </Button>
          </div>
          );
        })()}

        <div className="workout-exercises">
          {(activeWorkout?.exercises || []).map((ex, exIndex) => (
            <div key={ex.exercise_id}>
            <Card className="exercise-log-card">
              <CardHeader className="exercise-log-header">
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  {(ex.image_url || exerciseImg[ex.exercise_id]) && (
                    <img src={ex.image_url || exerciseImg[ex.exercise_id]} alt={ex.exercise_name}
                      loading="lazy" title="Kliknij, aby powiększyć"
                      onClick={() => setGifPreview({ url: ex.image_url || exerciseImg[ex.exercise_id], name: ex.exercise_name })}
                      style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: "#fff", border: "1px solid var(--border)", cursor: "zoom-in" }} />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <CardTitle>{ex.exercise_name}</CardTitle>
                    <CardDescription>{ex.exercise_category}</CardDescription>
                  </div>
                </div>
                <div className="exercise-actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openExerciseTimer(ex)}
                    className="exercise-timer-btn"
                    data-testid={`exercise-timer-${ex.exercise_id}`}
                  >
                    <Timer size={18} />
                  </Button>
                  {ex.video_url && (
                    <Button variant="ghost" size="sm" asChild>
                      <a href={ex.video_url} target="_blank" rel="noopener noreferrer"><Video size={18} /></a>
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => removeExerciseFromWorkout(ex.exercise_id)} className="exercise-remove-btn">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="sets-grid">
                  <div className="sets-header">
                    <span>Seria</span>
                    <span>kg</span>
                    <span>Powt.</span>
                    <span></span>
                  </div>
                  {workoutLog[ex.exercise_id]?.map((set, setIndex) => (
                    <div key={setIndex} className={`set-row ${set.completed ? 'completed' : ''}`}>
                      {!set.completed && ex.last_logged_sets?.[setIndex] && (
                        <div className="prev-ref">Ostatnio: {ex.last_logged_sets[setIndex].actual_weight || '?'}kg x {ex.last_logged_sets[setIndex].actual_reps || '?'}</div>
                      )}
                      <span className="set-number">{set.set_number}</span>
                      <Input
                        type="number"
                        value={set.actual_weight}
                        onChange={(e) => updateSetValue(ex.exercise_id, setIndex, 'actual_weight', e.target.value)}
                        placeholder="kg"
                        className="set-input"
                        disabled={set.completed}
                      />
                      <Input
                        type="number"
                        value={set.actual_reps}
                        onChange={(e) => updateSetValue(ex.exercise_id, setIndex, 'actual_reps', e.target.value)}
                        placeholder="reps"
                        className="set-input"
                        disabled={set.completed}
                      />
                      <div className="set-actions">
                        {!set.completed ? (
                          <Button
                            variant="default"
                            size="sm"
                            className="one-tap-btn"
                            onClick={() => oneTapComplete(ex.exercise_id, setIndex)}
                            data-testid={`one-tap-${ex.exercise_id}-${setIndex}`}
                          >
                            <Check size={16} />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="check-btn completed"
                            onClick={() => toggleSet(ex.exercise_id, setIndex)}
                          >
                            <Check size={16} />
                          </Button>
                        )}
                        {!set.completed && (workoutLog[ex.exercise_id]?.length > 1) && (
                          <Button variant="ghost" size="sm" className="remove-set-btn" onClick={() => removeSetFromExercise(ex.exercise_id, setIndex)}>
                            <Minus size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="add-set-btn" onClick={() => addSetToExercise(ex.exercise_id)}>
                  <Plus size={14} /> Seria
                </Button>
              </CardContent>
            </Card>
            {exIndex < (activeWorkout?.exercises || []).length - 1 && (
              <div className="next-exercise-preview">
                Nastepne
                <strong>{activeWorkout.exercises[exIndex + 1].exercise_name}</strong>
              </div>
            )}
            </div>
          ))}
        </div>

        {/* Exercise Picker Dialog */}
        <Dialog open={exercisePickerOpen} onOpenChange={setExercisePickerOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dodaj ćwiczenie</DialogTitle>
              <DialogDescription>Wybierz ćwiczenie z biblioteki</DialogDescription>
            </DialogHeader>
            <ScrollArea className="exercise-picker-list">
              {availableExercises
                .filter(ex => !(activeWorkout?.exercises || []).find(ae => ae.exercise_id === ex.id))
                .map(ex => (
                  <div key={ex.id} className="exercise-picker-item" onClick={() => addExerciseToWorkout(ex)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      {ex.image_url && (
                        <img src={ex.image_url} alt={ex.name} loading="lazy" title="Kliknij, aby powiększyć"
                          onClick={(e) => { e.stopPropagation(); setGifPreview({ url: ex.image_url, name: ex.name }); }}
                          style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: "#fff", border: "1px solid var(--border)", cursor: "zoom-in" }} />
                      )}
                      <div className="exercise-picker-info">
                        <span className="exercise-picker-name">{ex.name}</span>
                        <span className="exercise-picker-category">{ex.category}</span>
                      </div>
                    </div>
                    <Plus size={18} />
                  </div>
                ))}
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Exercise GIF preview */}
        {gifPreview && (
          <div onClick={() => setGifPreview(null)}
            style={{ position: "fixed", inset: 0, zIndex: 140, background: "rgba(0,0,0,0.8)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <button type="button" onClick={() => setGifPreview(null)} aria-label="Zamknij"
              style={{ position: "absolute", top: 16, right: 16, width: 40, height: 40, borderRadius: "50%", border: "none", cursor: "pointer", background: "rgba(255,255,255,0.15)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={22} />
            </button>
            <img src={gifPreview.url} alt={gifPreview.name} onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "92vw", maxHeight: "78vh", borderRadius: 14, background: "#fff", objectFit: "contain", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }} />
            <div style={{ color: "#fff", fontSize: 16, fontWeight: 600, marginTop: 14, textAlign: "center" }}>{gifPreview.name}</div>
          </div>
        )}
      </div>
    );
  }

  // Main Dashboard View
  return (
    <div className="app-container mode-gentle" data-testid="client-dashboard">
      <header className="app-header">
        <div className="header-brand">
          <Dumbbell size={24} strokeWidth={1.5} />
          <span>DuoFit</span>
        </div>
        <div className="header-actions">
          {!user.trainer_id && (
            <Button variant="outline" size="sm" onClick={() => setConnectOpen(true)} data-testid="connect-btn">
              <UserPlus size={16} /> Connect
            </Button>
          )}
          <Button variant="ghost" size="sm" className="notif-bell" onClick={openClientNotifications} data-testid="client-notif-bell">
            <Bell size={18} />
            {clientUnreadCount > 0 && <span className="notif-badge">{clientUnreadCount}</span>}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
            <LogOut size={18} />
          </Button>
        </div>
      </header>

      {/* Offline banner */}
      {typeof navigator !== 'undefined' && !navigator.onLine && (
        <div style={{background:'var(--warning)', color:'#000', textAlign:'center', padding:'6px 12px', fontSize:12, fontWeight:600}}>
          Tryb offline — dane zostaną zsynchronizowane po powrocie sieci
        </div>
      )}

      <main className="main-content">
        <GuestBanner />
        {orphanSession && runningView !== "active_session" && (
          <div
            data-testid="orphan-session-banner"
            style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              padding: "10px 14px", marginBottom: 16, borderRadius: 8,
              background: "var(--warning-bg, #fff7ed)", border: "1px solid var(--warning-border, #fed7aa)",
            }}
          >
            <div style={{ flex: 1, minWidth: 220, fontSize: 13, lineHeight: 1.5 }}>
              <strong>Niedokończony trening.</strong>{" "}
              {activityTypeLabels[orphanSession.activity_type] || "Trening"} rozpoczęty{" "}
              {format(new Date(orphanSession.started_at), "d MMM, HH:mm")} nie został zakończony.
            </div>
            <Button size="sm" variant="outline" onClick={abandonOrphanSession}>Porzuć</Button>
            <Button size="sm" onClick={resumeOrphanSession}>Dokończ</Button>
          </div>
        )}
        {/* ===== TODAY TAB (3 elements only) ===== */}
        {activeTab === "today" && (
          <>
            <section className="today-hero">
              <div className="today-header-row">
                <div>
                  <p className="today-greeting">{(() => { const h = new Date().getHours(); const n = user.name.split(' ')[0]; if (h < 6) return `Wczesny ptaszek, ${n}!`; if (h < 12) return `Dzien dobry, ${n}!`; if (h < 17) return `Czesc, ${n}!`; if (h < 21) return `Dobry wieczor, ${n}!`; return `Odpoczywasz, ${n}?`; })()}</p>
                  <p className="today-date">{format(new Date(), "EEEE, d MMMM").toUpperCase()}</p>
                </div>
                {gamification?.workout_streak > 0 && (
                  <div className="streak-badge-pill">
                    <Flame size={16} />
                    <span>{gamification.workout_streak}</span>
                  </div>
                )}
              </div>

              {/* Hero readiness ring — single ring */}
              {(() => {
                const s = readiness?.score != null ? readiness.score : null;
                const hasScore = s != null;
                const sp = readiness?.sleep_pct ?? 0;
                const mp = readiness?.mood_pct ?? 0;
                const hp = readiness?.hydration_pct ?? 0;
                const rr = 72;
                const cc = 2 * Math.PI * rr;
                const ringPct = hasScore ? s : 0;
                const supportMsg = hasScore
                  ? (s >= 70 ? 'Swietny dzien na trening' : s >= 40 ? 'Sluchaj swojego ciala' : 'Odpoczynek tez jest wazny')
                  : 'Wypelnij Health Check';
                return (
                  <div className="readiness-pod-v2">
                    <div className="readiness-ring-wrap">
                      <svg width="180" height="180" viewBox="0 0 180 180" className="readiness-ring-svg">
                        <defs>
                          <linearGradient id="grad-readiness" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#11998E" />
                            <stop offset="100%" stopColor="#38EF7D" />
                          </linearGradient>
                        </defs>
                        <circle cx="90" cy="90" r={rr} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
                        <circle cx="90" cy="90" r={rr} fill="none" stroke="url(#grad-readiness)" strokeWidth="10"
                          strokeLinecap="round" strokeDasharray={cc} strokeDashoffset={cc - (ringPct / 100) * cc}
                          transform="rotate(-90 90 90)" className="readiness-ring-arc" />
                        <text x="90" y="82" textAnchor="middle" dominantBaseline="central"
                          fill="var(--text)" fontSize={hasScore ? "44" : "18"} fontWeight="200">{hasScore ? s : '?'}</text>
                        <text x="90" y="108" textAnchor="middle" dominantBaseline="central"
                          fill="var(--text-muted)" fontSize="11" fontWeight="600" letterSpacing="1">GOTOWOSC</text>
                      </svg>
                    </div>
                    <div className="readiness-stats-row">
                      <div className="readiness-stat-item">
                        <Moon size={16} style={{color: '#667EEA'}} />
                        <span className="readiness-stat-value">{sp}%</span>
                        <span className="readiness-stat-label">SEN</span>
                      </div>
                      <div className="readiness-stat-divider" />
                      <div className="readiness-stat-item">
                        <Heart size={16} style={{color: '#FBBF24'}} />
                        <span className="readiness-stat-value">{mp}%</span>
                        <span className="readiness-stat-label">NASTROJ</span>
                      </div>
                      <div className="readiness-stat-divider" />
                      <div className="readiness-stat-item">
                        <Droplet size={16} style={{color: '#4ECDC4'}} />
                        <span className="readiness-stat-value">{hp}%</span>
                        <span className="readiness-stat-label">NAWODNIENIE</span>
                      </div>
                    </div>
                    <span className="readiness-msg">{supportMsg}</span>
                  </div>
                );
              })()}
            </section>

            {/* Nawodnienie — komponent i endpointy istniały od dawna, ale widget
                nie był renderowany w żadnym miejscu aplikacji. */}
            <HydrationWidget token={token} />

            {/* Nawyki — trener je przypisywał, a podopieczny nie miał ich gdzie
                zobaczyć ani odhaczyć: `toggleHabit` było napisane i nigdy nie
                wywołane, a pobrana lista nigdzie nie renderowana. */}
            {habits.length > 0 && (
              <section className="today-section" data-testid="habits-section">
                <h2><CheckCircle2 size={18} /> Nawyki na dziś</h2>
                <div className="tasks-list">
                  {habits.map(habit => {
                    const streak = habitStreaks[habit.id];
                    return (
                      <div key={habit.id} className="workout-cta-pod" data-testid={`habit-${habit.id}`}>
                        <div className="plan-card-icon"
                             style={{ background: habit.completed ? 'var(--gradient-primary)' : 'var(--surface-2, #2a2a35)' }}>
                          <CheckCircle2 size={22} color={habit.completed ? '#FFF' : 'var(--text-secondary)'} />
                        </div>
                        <div className="task-info">
                          <h3>{habit.name}</h3>
                          <p>
                            {streak?.current_streak > 0
                              ? `Seria: ${streak.current_streak} dni (rekord ${streak.longest_streak})`
                              : (habit.description || "Zacznij serię dzisiaj")}
                          </p>
                        </div>
                        <Button size="sm" variant={habit.completed ? "outline" : "default"}
                                className={habit.completed ? "" : "gradient-pill-btn"}
                                data-testid={`toggle-habit-${habit.id}`}
                                onClick={() => toggleHabit(habit)}>
                          {habit.completed ? "Cofnij" : "Zrobione"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* 1. Plan na dziś */}
            <section className="today-section">
              <h2><Dumbbell size={18} /> Plan na dziś</h2>
              {today?.pending_workouts?.length === 0 && today?.completed_workouts === 0 ? (
                <div className="empty-msg">
                  <p>Brak zaplanowanych treningow na dzis</p>
                </div>
              ) : (
                <div className="tasks-list fade-in-stagger">
                  {today?.pending_workouts?.map(plan => (
                    <div key={plan.id} className="workout-cta-pod" data-testid={`workout-${plan.id}`}>
                      <div className="plan-card-icon" style={{background: 'var(--gradient-primary)'}}>
                        <Dumbbell size={22} color="#FFF" />
                      </div>
                      <div className="task-info">
                        <h3>{plan.name}</h3>
                        <p>{plan.exercises?.length} cwiczen</p>
                      </div>
                      <Button onClick={() => startWorkout(plan)} className="gradient-pill-btn" size="sm">
                        <Play size={14} /> Start
                      </Button>
                    </div>
                  ))}
                  {today?.completed_workouts > 0 && (
                    <div className="completed-badge">
                      <Check size={16} />
                      <span>{today.completed_workouts} trening{today.completed_workouts > 1 ? 'i' : ''} ukończon{today.completed_workouts > 1 ? 'e' : 'y'}</span>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* 1b. My Bookings + Book button */}
            <section className="today-section">
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <h2><Calendar size={18} /> {t("booking.mySessions")}</h2>
                {user.trainer_id && (
                  <Button size="sm" onClick={() => setBookingOpen(true)}>
                    <Plus size={14} /> Zarezerwuj
                  </Button>
                )}
              </div>
              {myBookings.length === 0 ? (
                <div className="empty-msg">
                  <p>{user.trainer_id ? t("booking.noSessions") : t("booking.connectFirst")}</p>
                </div>
              ) : (
                <div className="tasks-list fade-in-stagger">
                  {myBookings.map(b => (
                    <div key={b.id} className="workout-cta-pod" style={{borderLeft: `3px solid ${b.status === 'confirmed' ? 'var(--success)' : b.status === 'pending_confirmation' ? 'var(--warning)' : 'var(--text-secondary)'}`}}>
                      <div className="plan-card-icon" style={{background: b.status === 'confirmed' ? 'var(--success)' : 'var(--warning)'}}>
                        <Calendar size={22} color="#FFF" />
                      </div>
                      <div className="task-info">
                        <h3>{new Date(b.date + 'T00:00').toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short' })} — {b.time}</h3>
                        <p>
                          <Badge variant={b.status === 'confirmed' ? 'default' : b.status === 'pending_confirmation' ? 'secondary' : 'outline'} style={{fontSize:11}}>
                            {b.status === 'confirmed' ? t("status.confirmed") : b.status === 'pending_confirmation' ? t("status.pending") : b.status === 'scheduled' ? t("status.scheduled") : b.status}
                          </Badge>
                          {b.duration && <span style={{marginLeft:8, fontSize:12, color:'var(--text-secondary)'}}>{b.duration} min</span>}
                        </p>
                      </div>
                      {(b.status === 'pending_confirmation' || b.status === 'confirmed' || b.status === 'scheduled') && (
                        <Button variant="ghost" size="sm" onClick={() => cancelMyBooking(b.id)}>
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* 2. Health Check */}
            <section style={{padding: '0 20px 16px'}}>
              <HealthCheckWidget token={token} />
            </section>

            {/* 4. Ten Tydzien */}
            {snapshot && (
              <section className="quick-stats-section">
                <h3 className="quick-stats-title">TEN TYDZIEN</h3>
                <div className="quick-stats-row">
                  <div className="quick-stat-item">
                    <span className="quick-stat-value" style={{color: '#FF3366'}}>{snapshot.workouts_completed || 0}</span>
                    <span className="quick-stat-label">TRENINGI</span>
                  </div>
                  <div className="quick-stat-item">
                    <span className="quick-stat-value" style={{color: '#FF9500'}}>{Math.round((snapshot.total_volume || 0) / 100)}k</span>
                    <span className="quick-stat-label">OBJETOSC</span>
                  </div>
                  <div className="quick-stat-item">
                    <span className="quick-stat-value" style={{color: '#4ECDC4'}}>{snapshot.workouts_planned || 0}</span>
                    <span className="quick-stat-label">ZAPLANOWANE</span>
                  </div>
                </div>
              </section>
            )}

          </>
        )}

        {/* ===== TRENING / CARDIO / PROGRESS TABS ===== */}
        {(activeTab === "trening" || activeTab === "cardio" || activeTab === "progress") && (
          <div className="trening-section">
            {runningView === "list" && (
          <>
            <section className="tab-header">
              <h1>{activeTab === "trening" ? "Trening" : activeTab === "cardio" ? "Cardio" : "Postep"}</h1>
            </section>

            {/* === TRENING TAB CONTENT === */}
            {activeTab === "trening" && gamification && (() => {
              const xpPct = gamification.xp_progress_percent || 0;
              const streakPct = Math.min(100, (gamification.workout_streak || 0) / 7 * 100);
              const workoutPct = ((gamification.total_workouts || 0) % 10) * 10;
              const makeRing = (radius, pct, color) => {
                const c = 2 * Math.PI * radius;
                const off = c - (pct / 100) * c;
                return { c, off, color };
              };
              const outer = makeRing(44, xpPct, '#667EEA');
              const middle = makeRing(36, streakPct, '#4ADE80');
              const inner = makeRing(28, workoutPct, '#FBBF24');
              return (
              <section className="activity-rings-bar" data-testid="gamification-bar" onClick={() => {
                const el = document.querySelector('.gamification-full');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}>
                <div className="rings-container">
                  <svg width="100" height="100" viewBox="0 0 100 100" className="activity-rings-svg">
                    <circle cx="50" cy="50" r={outer.c / (2*Math.PI)} fill="none" stroke="rgba(124,110,175,0.15)" strokeWidth="7" />
                    <circle cx="50" cy="50" r={outer.c / (2*Math.PI)} fill="none" stroke={outer.color} strokeWidth="7"
                      strokeLinecap="round" strokeDasharray={outer.c} strokeDashoffset={outer.off}
                      transform="rotate(-90 50 50)" className="ring-arc" />
                    <circle cx="50" cy="50" r={middle.c / (2*Math.PI)} fill="none" stroke="rgba(74,222,128,0.15)" strokeWidth="7" />
                    <circle cx="50" cy="50" r={middle.c / (2*Math.PI)} fill="none" stroke={middle.color} strokeWidth="7"
                      strokeLinecap="round" strokeDasharray={middle.c} strokeDashoffset={middle.off}
                      transform="rotate(-90 50 50)" className="ring-arc" />
                    <circle cx="50" cy="50" r={inner.c / (2*Math.PI)} fill="none" stroke="rgba(251,191,36,0.15)" strokeWidth="7" />
                    <circle cx="50" cy="50" r={inner.c / (2*Math.PI)} fill="none" stroke={inner.color} strokeWidth="7"
                      strokeLinecap="round" strokeDasharray={inner.c} strokeDashoffset={inner.off}
                      transform="rotate(-90 50 50)" className="ring-arc" />
                    <text x="50" y="50" textAnchor="middle" dominantBaseline="central"
                      fill="var(--text)" fontSize="18" fontWeight="800">{gamification.level}</text>
                  </svg>
                </div>
                <div className="rings-legend">
                  <div className="ring-legend-item"><span className="ring-dot" style={{background:'#667EEA'}} />{gamification.xp} XP</div>
                  <div className="ring-legend-item"><span className="ring-dot" style={{background:'#4ADE80'}} />{gamification.workout_streak} dni</div>
                  <div className="ring-legend-item"><span className="ring-dot" style={{background:'#FBBF24'}} />{gamification.total_workouts} treningow</div>
                </div>
              </section>
              );
            })()}

            {activeTab === "trening" && snapshot && (
              <section className="snapshot-section">
                <h2><BarChart3 size={18} /> Ten tydzien</h2>
                <div className="snapshot-rings">
                  <MiniRing size={64} color="#667EEA"
                    pct={snapshot.workouts_planned ? (snapshot.workouts_completed / snapshot.workouts_planned * 100) : 0}
                    label={`${snapshot.workouts_completed}/${snapshot.workouts_planned || '-'}`}
                    sublabel="Treningi" />
                  <MiniRing size={64} color="#4ADE80"
                    pct={Math.min(100, snapshot.total_volume / 20000 * 100)}
                    label={`${Math.round(snapshot.total_volume / 1000)}k`}
                    sublabel="Objetosc" />
                  {snapshot.volume_change_percent !== 0 && (
                    <MiniRing size={64}
                      color={snapshot.volume_change_percent > 0 ? '#4ADE80' : '#EF4444'}
                      pct={Math.min(100, Math.abs(snapshot.volume_change_percent) * 2)}
                      label={`${snapshot.volume_change_percent > 0 ? '+' : ''}${snapshot.volume_change_percent}%`}
                      sublabel="vs tydz." />
                  )}
                </div>
              </section>
            )}

            {activeTab === "trening" && gamification && (
              <section className="gamification-full" data-testid="gamification-full">
                {/* Weekly bar chart */}
                {progressHistory?.workout_logs && (
                  <div className="weekly-bar-card">
                    <h3 className="weekly-bar-title">TEN TYDZIEN</h3>
                    <div className="weekly-bars">
                      {(() => {
                        const days = ['Pn','Wt','Sr','Cz','Pt','So','Nd'];
                        const now = new Date();
                        const monday = new Date(now);
                        monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
                        monday.setHours(0,0,0,0);
                        const bars = days.map((label, i) => {
                          const day = new Date(monday);
                          day.setDate(monday.getDate() + i);
                          const dayStr = day.toISOString().slice(0,10);
                          const logs = (progressHistory.workout_logs || [])
                            .filter(l => l.completed_at?.startsWith(dayStr));
                          return { label, count: logs.length };
                        });
                        const maxVal = Math.max(1, ...bars.map(b => b.count));
                        return bars.map((b, i) => (
                          <div key={i} className="bar-col">
                            <div className="bar-fill-wrap">
                              <div className="bar-fill" style={{height: `${(b.count / maxVal) * 100}%`, background: b.count > 0 ? 'var(--gradient-primary)' : 'transparent'}} />
                            </div>
                            <span className="bar-label">{b.label}</span>
                          </div>
                        ));
                      })()}
                    </div>
                    <div className="weekly-bar-stats">
                      <span><strong>{snapshot?.workouts_completed || 0}</strong> treningi</span>
                      <span><strong>{Math.round((gamification.total_volume || 0) / 1000)}k</strong> kg</span>
                    </div>
                  </div>
                )}

                {/* Level ring + XP bar */}
                <div className="level-section-v2">
                  <div className="level-ring-row">
                    <MiniRing size={100} color="#667EEA"
                      gradientId="grad-level" gradientColors={['#CCFF00', '#FF9500']}
                      pct={gamification.xp_progress_percent || 0}
                      label={gamification.level} fontSize={28} />
                    <div className="level-stats-col">
                      <div className="level-stat-row">
                        <Dumbbell size={16} style={{color: '#667EEA'}} />
                        <span><strong>{gamification.total_workouts}</strong> Treningow</span>
                      </div>
                      <div className="level-stat-row">
                        <Flame size={16} style={{color: '#FF9500'}} />
                        <span><strong>{gamification.workout_streak}</strong> dni Seria</span>
                      </div>
                    </div>
                  </div>
                  <div className="xp-progress-bar">
                    <div className="xp-track">
                      <div className="xp-fill" style={{width: `${gamification.xp_progress_percent || 0}%`}} />
                    </div>
                    <span className="xp-text">{gamification.xp} / {gamification.xp + gamification.xp_for_next_level} XP</span>
                  </div>
                </div>

                {/* Badges horizontal scroll */}
                {gamification.badges?.length > 0 && (
                  <div className="badges-section">
                    <h2>Odznaki</h2>
                    <div className="badges-scroll">
                      {gamification?.badges?.map(badge => (
                        <div key={badge.id} className="badge-item-scroll" data-testid={`badge-${badge.id}`} onClick={() => toast.info(`${badge.name}`)}>
                          <div className="badge-circle">
                            {badge.icon === 'dumbbell' && <Dumbbell size={24} />}
                            {badge.icon === 'fire' && <Flame size={24} />}
                            {badge.icon === 'trophy' && <Trophy size={24} />}
                            {badge.icon === 'flame' && <Flame size={24} />}
                            {badge.icon === 'star' && <Star size={24} />}
                            {badge.icon === 'weight' && <Weight size={24} />}
                            {badge.icon === 'camera' && <Camera size={24} />}
                            {badge.icon === 'check' && <Check size={24} />}
                          </div>
                          <span className="badge-name-v2">{badge.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* === MOJE PLANY (Trening tab) === */}
            {activeTab === "trening" && (
              <section style={{ padding: "0 20px 12px" }}>
                <Button onClick={openPlanBuilder} variant="outline" style={{ width: "100%" }}>
                  <Plus size={16} /> Stwórz własny plan
                </Button>
              </section>
            )}

            {activeTab === "trening" && allWorkoutPlans.length > 0 && (
              <section className="my-plans-section">
                <h2>Plany treningowe</h2>
                <div className="my-plans-list fade-in-stagger">
                  {allWorkoutPlans.map(plan => {
                    // Trenerskie plany nie są tu edytowalne — trener nimi steruje,
                    // klient tylko odpala trening (patrz PUT /workout-plans w
                    // backendzie: dopuszcza to wyłącznie właściciela).
                    const editable = !plan.trainer_id;
                    return (
                    <div key={plan.id || plan._id} className="plan-card-v2" onClick={() => startWorkout(plan)} style={{cursor:'pointer'}}>
                      <div className="plan-card-icon" style={{background: 'var(--gradient-primary)'}}>
                        <Dumbbell size={22} color="#FFF" />
                      </div>
                      <div className="plan-card-info">
                        <h3>{plan.name}</h3>
                        <p>{plan.exercises?.length || 0} cwiczen</p>
                      </div>
                      {editable && (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openPlanBuilder(plan); }}
                                data-testid={`edit-plan-${plan.id}`} title="Edytuj plan">
                          <Edit size={16} />
                        </Button>
                      )}
                      <Button onClick={(e) => { e.stopPropagation(); startWorkout(plan); }} className="gradient-pill-btn" size="sm">
                        <Play size={14} /> Start
                      </Button>
                    </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === PROGRESS TAB CONTENT === */}
            {activeTab === "progress" && (<section className="progress-section">
              <div className="section-header">
                <h2>Zdjęcia postępów</h2>
                <Button variant="outline" size="sm" onClick={() => setPhotoUploadOpen(true)} data-testid="add-photo-btn">
                  <Camera size={16} /> Dodaj
                </Button>
              </div>
              
              {photoComparison?.before && photoComparison?.after ? (
                <div className="photo-comparison">
                  <div className="comparison-slider" style={{ '--position': `${morphPosition}%` }}>
                    <img src={photoComparison.after.photo_url} alt="Po" className="photo-after" />
                    <img src={photoComparison.before.photo_url} alt="Przed" className="photo-before" />
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={morphPosition}
                      onChange={(e) => { setIsAnimating(false); setMorphPosition(Number(e.target.value)); }}
                      className="slider-input"
                    />
                  </div>
                  <div className="comparison-labels">
                    <span>Przed</span>
                    <span>{photoComparison.total_days} dni</span>
                    <span>Teraz</span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={startMorphAnimation}
                    className="morph-btn"
                    data-testid="morph-btn"
                  >
                    <Repeat size={16} /> {isAnimating ? 'Stop' : 'Animuj'}
                  </Button>
                </div>
              ) : (
                <div className="empty-msg">
                  <Camera size={28} />
                  <p>Dodaj zdjecia aby sledzic postepy</p>
                </div>
              )}
            </section>
            )}

            {/* Body metrics (Progress tab) */}
            {activeTab === "progress" && latestMeasurements.length > 0 && (
              <section className="body-metrics-section">
                <h2>Pomiary ciala</h2>
                <div className="body-metrics-cards">
                  {[
                    {key: "weight", label: "WAGA", unit: "kg"},
                    {key: "waist", label: "TALIA", unit: "cm"},
                    {key: "hips", label: "BIODRA", unit: "cm"},
                  ].filter(m => latestMeasurements[0]?.[m.key]).map(metric => {
                    const vals = [...latestMeasurements].reverse().map(m => m[metric.key]).filter(Boolean);
                    const current = latestMeasurements[0][metric.key];
                    const prev = latestMeasurements[1]?.[metric.key];
                    const diff = prev ? (current - prev).toFixed(1) : null;
                    const isGood = diff ? current <= prev : true;
                    return (
                      <div className="body-metric-card" key={metric.key}>
                        <span className="body-metric-label">{metric.label}</span>
                        <div className="body-metric-value-row">
                          <span className="body-metric-value">{current}</span>
                          <span className="body-metric-unit">{metric.unit}</span>
                        </div>
                        {diff && (
                          <div className={`body-metric-trend ${isGood ? 'good' : 'bad'}`}>
                            <TrendingUp size={14} />
                            <span>{diff}</span>
                          </div>
                        )}
                        {vals.length >= 2 && (() => {
                          const w = 100, h = 28, pad = 2;
                          const mn = Math.min(...vals), mx = Math.max(...vals);
                          const range = mx - mn || 1;
                          const pts = vals.map((v, i) => ({
                            x: pad + (i / (vals.length - 1)) * (w - pad * 2),
                            y: pad + (1 - (v - mn) / range) * (h - pad * 2)
                          }));
                          const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
                          const color = isGood ? '#4ADE80' : '#FF6B6B';
                          return (
                            <svg viewBox={`0 0 ${w} ${h}`} className="metric-sparkline">
                              <defs>
                                <linearGradient id={`spark-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={color} stopOpacity="0.25" />
                                  <stop offset="100%" stopColor={color} stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              <path d={`${line} L${pts[pts.length-1].x.toFixed(1)},${h} L${pts[0].x.toFixed(1)},${h} Z`} fill={`url(#spark-${metric.key})`} />
                              <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <circle cx={pts[pts.length-1].x.toFixed(1)} cy={pts[pts.length-1].y.toFixed(1)} r="2" fill={color} />
                            </svg>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {(activeTab === "trening" || activeTab === "progress") && progressHistory?.workout_logs?.length > 0 && (
              <section className="history-section">
                <h2>Historia treningów</h2>
                <div className="history-list fade-in-stagger">
                  {(progressHistory?.workout_logs || []).slice(0, 5).map((log, i) => (
                    <div key={i} className="history-row clickable-card" onClick={() => setExpandedLogIndex(expandedLogIndex === i ? null : i)}>
                      <div className="history-row-main">
                        <MiniRing size={44} color="#667EEA" pct={Math.min(100, (log.total_volume || 0) / 10000 * 100)}
                          label={`${Math.round((log.total_volume || 0) / 1000)}k`} fontSize={10} />
                        <div className="history-info">
                          <h4>{log.workout_name || 'Trening'}</h4>
                          <p>{format(new Date(log.completed_at), "d MMM yyyy, HH:mm")} · {log.exercises_count || 0} cw.</p>
                        </div>
                        <ChevronDown size={16} className="history-expand-icon" style={{ transform: expandedLogIndex === i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                      </div>
                      {expandedLogIndex === i && log.exercises && (
                        <div className="history-detail">
                          {log.exercises.map((ex, j) => (
                            <div key={j} className="exercise-row">
                              <strong>{ex.exercise_name}</strong>
                              <span>{ex.sets?.length || 0} serii</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {activeTab === "progress" && (
            <section className="technique-section">
              <div className="technique-card-gradient" onClick={() => setTechniqueUploadOpen(true)}>
                <div className="technique-icon-circle">
                  <Video size={28} color="#FFF" />
                </div>
                <h3>SPRAWDZENIE TECHNIKI</h3>
                <p>Nagraj swoje cwiczenie, a trener sprawdzi Twoja technike i da feedback</p>
                <Button variant="outline" size="sm" className="technique-btn" data-testid="upload-video-btn">
                  <Video size={16} /> Nagraj wideo
                </Button>
              </div>
            </section>
            )}

            {/* === CARDIO TAB CONTENT === */}
            {activeTab === "cardio" && (
            <section className="cardio-section">
              <h2><Activity size={18} /> Cardio</h2>

              {/* Activity Type Selector */}
              <div className="activity-type-selector" key={`activities-${activityCatalogueVersion}`}>
                  {Object.entries(activityTypeLabels).map(([key, label]) => {
                    const Icon = activityTypeIcons[key] || Activity;
                    return (
                      <button key={key} className={`activity-type-pill ${selectedActivityType === key ? 'active' : ''}`}
                        onClick={() => setSelectedActivityType(key)}>
                        <Icon size={16} />
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>

                {runningStats && runningStats.total_runs > 0 && (
                  <div className="cardio-big-stats">
                    <div className="cardio-big-row">
                      <div className="big-stat">
                        <span className="big-stat-value">{(runningStats.by_activity_type?.[selectedActivityType]?.distance_km || 0).toFixed(1)}</span>
                        <span className="big-stat-unit">km</span>
                        <span className="big-stat-label">DYSTANS</span>
                      </div>
                      <div className="big-stat-divider" />
                      <div className="big-stat">
                        <span className="big-stat-value">{runningStats.best_pace?.toFixed(1) || '-'}</span>
                        <span className="big-stat-unit">/km</span>
                        <span className="big-stat-label">SR. TEMPO</span>
                      </div>
                    </div>
                    <div className="cardio-small-stats">
                      <div className="small-stat">
                        <Footprints size={16} style={{color:'#00D4FF'}} />
                        <span>{runningStats.by_activity_type?.[selectedActivityType]?.count || 0} biegi</span>
                      </div>
                      <div className="small-stat">
                        <Clock size={16} style={{color:'#4ECDC4'}} />
                        <span>{Math.round((runningStats.total_duration_seconds || 0) / 60)} min</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Weekly distance chart */}
                {runningSessions.length > 0 && (() => {
                  const shortDay = ["Nd","Pn","Wt","Sr","Cz","Pt","So"];
                  const now = new Date();
                  const dayLabels = [];
                  const dayStarts = [];
                  for (let i = 6; i >= 0; i--) {
                    const d = new Date(now);
                    d.setDate(now.getDate() - i);
                    d.setHours(0,0,0,0);
                    dayStarts.push(d);
                    dayLabels.push(shortDay[d.getDay()]);
                  }
                  const dailyDist = Array(7).fill(0);
                  runningSessions.forEach(s => {
                    const d = new Date(s.started_at);
                    for (let i = 0; i < 7; i++) {
                      const end = new Date(dayStarts[i]);
                      end.setDate(end.getDate() + 1);
                      if (d >= dayStarts[i] && d < end) {
                        dailyDist[i] += s.total_distance_km || 0;
                        break;
                      }
                    }
                  });
                  const maxVal = Math.max(...dailyDist, 1);
                  const w = 280, h = 120, padX = 20, padY = 16;
                  const chartW = w - padX * 2, chartH = h - padY * 2;
                  const pts = dailyDist.map((v, i) => ({
                    x: padX + (i / 6) * chartW,
                    y: padY + chartH - (v / maxVal) * chartH
                  }));
                  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
                  const area = `${line} L${pts[6].x},${padY + chartH} L${pts[0].x},${padY + chartH} Z`;
                  return (
                    <div className="cardio-chart-section glass-card" style={{padding:'16px',marginBottom:'12px',borderRadius:'16px',background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                      <h4 style={{fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.15em',color:'rgba(255,255,255,0.4)',marginBottom:'12px',fontWeight:600}}>Dystans tygodnia</h4>
                      <svg viewBox={`0 0 ${w} ${h + 20}`} style={{width:'100%',maxWidth:320}}>
                        <defs>
                          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        {[0.25,0.5,0.75].map(f => (
                          <line key={f} x1={padX} y1={padY + chartH * (1-f)} x2={padX + chartW} y2={padY + chartH * (1-f)} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                        ))}
                        <path d={area} fill="url(#chartGrad)" />
                        <path d={line} fill="none" stroke="#00D4FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        {pts.map((p, i) => (
                          <g key={i}>
                            {dailyDist[i] > 0 && <circle cx={p.x} cy={p.y} r={3} fill="#00D4FF" />}
                            <text x={p.x} y={h + 14} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="10">{dayLabels[i]}</text>
                          </g>
                        ))}
                      </svg>
                    </div>
                  );
                })()}

                <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                  <Button className="free-run-btn" style={{flex:1}} onClick={() => startRunningSession(null)}>
                    <Play size={20} /> {freeSessionLabel(selectedActivityType)}
                  </Button>
                </div>

                <div className="section-header">
                  <h2>Plany {activityTypeLabels[selectedActivityType]?.toLowerCase() || "treningowe"}</h2>
                  <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                    <div style={{position:'relative'}}>
                      <Search size={16} style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:'var(--text-secondary)'}} />
                      <Input placeholder="Szukaj..." value={searchRunningPlans} onChange={e => setSearchRunningPlans(e.target.value)} style={{paddingLeft:32,width:160}} />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => {
                      setEditingRunningPlan(null);
                      setNewRunningPlan({ name: "", description: "", goal: "", difficulty: "medium", intervals: [], tags: [], estimated_distance_km: "", estimated_duration_minutes: "", activity_type: selectedActivityType });
                      setRunningView("plan_editor");
                    }}>
                      <Plus size={16} /> Nowy plan
                    </Button>
                  </div>
                </div>

                {(() => {
                  const filtered = searchRunningPlans
                    ? runningPlans.filter(p => p.name?.toLowerCase().includes(searchRunningPlans.toLowerCase()))
                    : runningPlans;
                  return filtered.length === 0 ? (
                  <div className="empty-msg"><RouteIcon size={32} /><p>Brak planow</p><p className="text-muted-sm">Utworz wlasny plan lub rozpocznij wolny trening</p></div>
                ) : (
                  <div className="running-plans-list fade-in-stagger">
                    {filtered.map(plan => (
                      <div key={plan.id} className="plan-row" onClick={() => { setSelectedRunningPlan(plan); setRunningView("plan_detail"); }}>
                        <MiniRing size={48} color={plan.difficulty === 'hard' ? '#EF4444' : plan.difficulty === 'medium' ? '#FBBF24' : '#4ADE80'}
                          pct={plan.difficulty === 'hard' ? 90 : plan.difficulty === 'medium' ? 60 : 30}
                          label={plan.estimated_distance_km || "?"} fontSize={11} sublabel="km" />
                        <div className="plan-row-info">
                          <div className="plan-card-header">
                            <h3>{plan.name}</h3>
                            <Badge variant={plan.type === "predefined" ? "secondary" : "default"}>{plan.type === "predefined" ? "Gotowy" : "Wlasny"}</Badge>
                          </div>
                          <p className="plan-meta-line"><Clock size={12} /> {plan.estimated_duration_minutes || "?"} min · {difficultyLabels[plan.difficulty] || plan.difficulty}</p>
                          {/* Plan progress bar */}
                          {(() => {
                            const sessionsForPlan = runningSessions.filter(s => s.plan_id === plan.id);
                            const totalIntervals = plan.intervals?.length || 1;
                            const completedPct = Math.min(100, Math.round((sessionsForPlan.length / Math.max(1, totalIntervals)) * 100));
                            return (
                              <div className="plan-progress-track">
                                <div className="plan-progress-fill" style={{width: `${completedPct}%`}} />
                                <span className="plan-progress-label">{sessionsForPlan.length}/{totalIntervals}</span>
                              </div>
                            );
                          })()}
                          {plan.tags?.length > 0 && (
                            <div className="plan-tags">{plan.tags.map(tag => <Badge key={tag} variant="secondary" className="plan-tag">{tag}</Badge>)}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
                })()}

                {runningSessions.length > 0 && (
                  <>
                    <h2 className="section-title">Ostatnie biegi</h2>
                    <div className="recent-runs-list fade-in-stagger">
                      {runningSessions.slice(0, 5).map(session => (
                        <div key={session.id} className="session-row" onClick={async () => {
                          setViewingSessionSummary(session);
                          setAiAnalysis(session.ai_analysis || null);
                          setRunningView("session_summary");
                          setSessionGpsTrack([]);
                          try {
                            const r = await api("get", `/running-sessions/${session.id}`, null, token);
                            setSessionGpsTrack(r.data?.gps_track || []);
                          } catch {}
                        }}>
                          <MiniRing size={44} color="#00D4FF" pct={Math.min(100, (session.total_distance_km || 0) / 10 * 100)}
                            label={`${(session.total_distance_km || 0).toFixed(1)}`} fontSize={9} sublabel="km" />
                          <div>
                            <p className="session-plan-name">{session.plan_snapshot?.name || "Wolny bieg"}</p>
                            <p className="session-date">{format(new Date(session.started_at), "d MMM")} · {session.average_pace?.toFixed(1)} min/km · {Math.floor((session.total_duration_seconds || 0) / 60)} min</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* AI Running Insights Card */}
                {aiRunningInsights && (
                  <div className="ai-insights-block">
                    <h3><Zap size={16} /> AI Insights</h3>
                    <div style={{whiteSpace:'pre-wrap', fontSize:14, lineHeight:1.6}}>{aiRunningInsights}</div>
                  </div>
                )}
            </section>

            )}

            {/* Files from Trainer — in progress tab */}
            {activeTab === "progress" && clientFiles.length > 0 && (
              <section className="files-inline-section" style={{marginTop: 32}}>
                <h2><FolderOpen size={18} /> Pliki od trenera</h2>
                <div className="files-grid">
                  {clientFiles.map(f => (
                    <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="file-card">
                      <div className="file-icon-box">
                        {f.file_type === 'pdf' && <ClipboardList size={24} />}
                        {f.file_type === 'image' && <Image size={24} />}
                        {f.file_type === 'video' && <Video size={24} />}
                        {!['pdf', 'image', 'video'].includes(f.file_type) && <FolderOpen size={24} />}
                      </div>
                      <div className="file-info">
                        <span className="file-name">{f.name}</span>
                        {f.notes && <span className="file-notes">{f.notes}</span>}
                        <Badge variant="secondary" className="file-type-badge">{f.file_type}</Badge>
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}
              </>
            )}

            {/* PLAN DETAIL VIEW (cardio sub-views) */}
            {activeTab === "cardio" && runningView === "plan_detail" && selectedRunningPlan && (
              <RunningPlanDetail
                deleteRunningPlan={deleteRunningPlan}
                selectedRunningPlan={selectedRunningPlan}
                setEditingRunningPlan={setEditingRunningPlan}
                setNewRunningPlan={setNewRunningPlan}
                setRunningView={setRunningView}
                startRunningSession={startRunningSession}
              />
            )}

            {/* PLAN EDITOR VIEW */}
            {runningView === "plan_editor" && (
              <RunningPlanEditor
                addInterval={addInterval}
                editingRunningPlan={editingRunningPlan}
                newInterval={newInterval}
                newRunningPlan={newRunningPlan}
                removeInterval={removeInterval}
                saveRunningPlan={saveRunningPlan}
                setNewInterval={setNewInterval}
                setNewRunningPlan={setNewRunningPlan}
                setRunningView={setRunningView}
              />
            )}

            {/* ACTIVE SESSION VIEW */}
            {activeTab === "cardio" && runningView === "active_session" && activeRunningSession && (
              <ActiveRunSession
                activeRunningSession={activeRunningSession}
                averagePace={averagePace}
                coachMessages={coachMessages}
                currentIntervalIndex={currentIntervalIndex}
                currentPace={currentPace}
                elapsedSeconds={elapsedSeconds}
                finishRunningSession={finishRunningSession}
                gpsTrack={gpsTrack}
                intervalElapsed={intervalElapsed}
                isRunningPaused={isRunningPaused}
                manualCadence={manualCadence}
                manualDistance={manualDistance}
                manualIncline={manualIncline}
                manualPower={manualPower}
                manualResistance={manualResistance}
                pauseRunningSession={pauseRunningSession}
                resumeRunningSession={resumeRunningSession}
                screenLocked={screenLocked}
                selectedActivityType={selectedActivityType}
                setManualCadence={setManualCadence}
                setManualDistance={setManualDistance}
                setManualIncline={setManualIncline}
                setManualPower={setManualPower}
                setManualResistance={setManualResistance}
                setScreenLocked={setScreenLocked}
                setVoiceEnabled={setVoiceEnabled}
                totalDistance={totalDistance}
                voiceEnabled={voiceEnabled}
              />
            )}

            {/* SESSION SUMMARY VIEW */}
            {activeTab === "cardio" && runningView === "session_summary" && viewingSessionSummary && (
              <RunSessionSummary
                aiAnalysis={aiAnalysis}
                aiAnalysisLoading={aiAnalysisLoading}
                fetchRunningData={fetchRunningData}
                requestAiAnalysis={requestAiAnalysis}
                sessionGpsTrack={sessionGpsTrack}
                setRunningView={setRunningView}
                viewingSessionSummary={viewingSessionSummary}
                downloadGpx={() => downloadSessionGpx(viewingSessionSummary)}
              />
            )}
          </div>
        )}

        {/* ===== DIETA TAB ===== */}
        {activeTab === "dieta" && (
          <DietTab
            dietGen={dietGen}
            dietKcal={dietKcal}
            setDietKcal={setDietKcal}
            genningDiet={genningDiet}
            generateMyDiet={generateMyDiet}
            savedDiets={savedDiets}
            savingDiet={savingDiet}
            saveMyDiet={saveMyDiet}
            todayNutrition={todayNutrition}
            setNutritionLogOpen={setNutritionLogOpen}
          />
        )}

        {/* ===== MESSAGES TAB ===== */}
        {activeTab === "messages" && (
          <ChatView user={user} token={token} />
        )}

        {/* ===== PROFIL TAB (Settings + Metrics + Hydration) ===== */}
        {activeTab === "profil" && (
          <>
            <SettingsView user={user} token={token} gamification={gamification} onUserUpdate={updateUser} onLogout={() => { logout(); navigate("/"); }} />

            {/* Kto widzi moje dane. Do tej pory klient nie miał ani jak tego
                sprawdzić, ani zmienić — grant odbierał wyłącznie trener. */}
            {transferRequests.length > 0 && (
              <section className="metrics-section" style={{ padding: "0 20px 24px" }}>
                <h2><UserPlus size={18} /> Prośba o zmianę trenera</h2>
                {transferRequests.map((req) => (
                  <div key={req.id} data-testid={`transfer-${req.id}`}
                       style={{ padding: "10px 0", borderBottom: "1px solid var(--border,#eee)" }}>
                    <div style={{ fontWeight: 600 }}>{req.to_trainer_name}</div>
                    <div className="text-muted-sm" style={{ marginBottom: 8 }}>
                      prosi o przejęcie prowadzenia od: {req.from_trainer_name}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button size="sm" data-testid={`transfer-accept-${req.id}`}
                              onClick={() => resolveTransfer(req, true)}>Zgadzam się</Button>
                      <Button size="sm" variant="outline" data-testid={`transfer-reject-${req.id}`}
                              onClick={() => resolveTransfer(req, false)}>Odrzuć</Button>
                    </div>
                  </div>
                ))}
              </section>
            )}

            <section className="metrics-section" style={{ padding: "0 20px 24px" }}>
              <h2><UserIcon size={18} /> Mój kod</h2>
              <p className="text-muted-sm" style={{ marginTop: 0 }}>
                Podaj go trenerowi, żeby podpiął Cię do siebie — bez szukania Cię po adresie e-mail.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <code data-testid="my-client-code"
                      style={{ fontSize: "1.15rem", fontWeight: 700, letterSpacing: 2, cursor: "pointer" }}
                      onClick={() => { navigator.clipboard.writeText(myClientCode); toast.success("Skopiowano"); }}>
                  {myClientCode || "—"}
                </code>
                <Button size="sm" variant="outline" data-testid="regenerate-code" onClick={regenerateMyCode}>
                  Nowy kod
                </Button>
              </div>
            </section>

            <section className="metrics-section" style={{ padding: "0 20px 24px" }}>
              <h2><Lock size={18} /> Dostęp do moich danych</h2>
              {myTrainers.length === 0 ? (
                <p className="text-muted-sm">Nikt poza Tobą nie ma dostępu do Twoich treningów i pomiarów.</p>
              ) : (
                myTrainers.map((tr) => (
                  <div key={tr.id} data-testid={`access-row-${tr.id}`}
                       style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
                                borderBottom: "1px solid var(--border,#eee)" }}>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <div style={{ fontWeight: 600 }}>{tr.name}</div>
                      <div className="text-muted-sm">
                        {tr.is_current ? "Twój obecny trener" : "Wgląd w postępy"}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" data-testid={`revoke-access-${tr.id}`}
                            onClick={() => revokeTrainerAccess(tr)}>
                      Odbierz dostęp
                    </Button>
                  </div>
                ))
              )}
              <p className="text-muted-sm" style={{ marginTop: 8 }}>
                Odebranie dostępu nie kasuje Twojej historii — treningi, pomiary i zdjęcia
                zostają na Twoim koncie i zobaczy je każdy kolejny trener, którego wybierzesz.
              </p>
            </section>

            {/* Custom Metrics (moved from Today) */}
            {customMetrics.length > 0 && (
              <section className="metrics-section" style={{padding: '0 20px 24px'}}>
                <h2><Activity size={18} /> Metryki</h2>
                <div className="metrics-today-grid">
                  {customMetrics.map(metric => {
                    const todayLog = todayMetricLogs.find(l => l.metric_id === metric.id);
                    const IconComp = clientMetricIconMap[metric.icon] || Activity;
                    return (
                      <div key={metric.id} className={`metric-ring-card clickable-card ${todayLog ? 'logged' : ''}`} onClick={() => {
                        setSelectedMetric(metric);
                        setMetricLogValue(todayLog ? String(todayLog.value) : "");
                        setMetricLogOpen(true);
                      }}>
                        <MiniRing size={52} color={todayLog ? '#4ADE80' : 'var(--border)'}
                          pct={todayLog ? 100 : 0}
                          label={todayLog ? todayLog.value : '—'} fontSize={12} />
                        <span className="metric-ring-name">{metric.name}</span>
                        {todayLog && <span className="metric-ring-unit">{metric.unit}</span>}
                        {!todayLog && <span className="metric-ring-unit" style={{color:'var(--primary)'}}>Uzupelnij</span>}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

          </>
        )}
      </main>

      {/* Tabata Timer */}
      {showTimer && <WorkoutTimer onClose={() => setShowTimer(false)} {...timerParams} />}

      {/* Bottom Navigation — 7 tabs */}
      <nav className="bottom-nav">
        <button className={`nav-tab ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>
          <Home size={20} />
          <span>{t("nav.today")}</span>
        </button>
        <button className={`nav-tab ${activeTab === 'trening' ? 'active' : ''}`} onClick={() => setActiveTab('trening')}>
          <Dumbbell size={20} />
          <span>{t("nav.workout")}</span>
        </button>
        <button className={`nav-tab ${activeTab === 'cardio' ? 'active' : ''}`} onClick={() => setActiveTab('cardio')}>
          <Activity size={20} />
          <span>{t("nav.cardio")}</span>
        </button>
        <button className={`nav-tab ${activeTab === 'progress' ? 'active' : ''}`} onClick={() => setActiveTab('progress')}>
          <TrendingUp size={20} />
          <span>{t("nav.progress")}</span>
        </button>
        <button className={`nav-tab ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => setActiveTab('messages')}>
          <MessageCircle size={20} />
          <span>{t("nav.messages")}</span>
        </button>
        <button className={`nav-tab ${activeTab === 'profil' ? 'active' : ''}`} onClick={() => setActiveTab('profil')} data-testid="nav-settings">
          <UserIcon size={20} />
          <span>{t("nav.profile")}</span>
        </button>
      </nav>

      {/* Connect Dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Połącz z trenerem</DialogTitle>
            <DialogDescription>Wprowadź kod od trenera</DialogDescription>
          </DialogHeader>
          <div className="field">
            <Input
              value={trainerCode}
              onChange={(e) => setTrainerCode(e.target.value.toUpperCase())}
              placeholder="ABC12345"
              data-testid="connect-code-input"
            />
          </div>
          {user.trainer_id && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, marginTop: 4 }}>
              <input type="checkbox" checked={keepPreviousAccess} data-testid="keep-previous-access"
                     onChange={(e) => setKeepPreviousAccess(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Zostaw dotychczasowemu trenerowi wgląd w moje postępy.
                <span style={{ display: "block", opacity: 0.65 }}>
                  Domyślnie zmiana trenera odbiera poprzedniemu dostęp do Twoich danych.
                </span>
              </span>
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>Anuluj</Button>
            <Button onClick={connectTrainer} data-testid="submit-connect">Połącz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={ratingOpen} onOpenChange={setRatingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("rating.title")}</DialogTitle>
            <DialogDescription>
              {ratingSession && `${new Date(ratingSession.date + 'T00:00').toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' })} o ${ratingSession.time}`}
            </DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field" style={{textAlign:'center'}}>
              <div style={{display:'flex', justifyContent:'center', gap:8, fontSize:32}}>
                {[1,2,3,4,5].map(s => (
                  <button
                    key={s}
                    onClick={() => setRatingStars(s)}
                    style={{background:'none', border:'none', cursor:'pointer', fontSize:32, opacity: s <= ratingStars ? 1 : 0.3, transition:'opacity 0.2s'}}
                  >
                    ⭐
                  </button>
                ))}
              </div>
              <p style={{fontSize:13, color:'var(--text-secondary)', marginTop:8}}>
                {ratingStars === 0 ? t("rating.select") : ratingStars === 5 ? t("rating.excellent") : ratingStars >= 4 ? t("rating.veryGood") : ratingStars >= 3 ? t("rating.ok") : ratingStars >= 2 ? t("rating.couldBeBetter") : t("rating.poor")}
              </p>
            </div>
            <div className="field">
              <Label>{t("rating.comment")} <span className="optional">({t("general.optional")})</span></Label>
              <Textarea value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} placeholder={t("rating.commentPlaceholder")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingOpen(false)}>{t("rating.skip")}</Button>
            <Button onClick={submitRating} disabled={ratingStars === 0}>{t("rating.submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("booking.title")}</DialogTitle>
            <DialogDescription>{t("booking.selectDate")}</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Data</Label>
              <Input
                type="date"
                value={bookingDate}
                min={new Date().toISOString().split('T')[0]}
                max={new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]}
                onChange={(e) => {
                  setBookingDate(e.target.value);
                  if (e.target.value) fetchAvailableSlots(e.target.value);
                  else setBookingSlots([]);
                }}
              />
            </div>

            {bookingDate && (
              <div className="field">
                <Label>{t("booking.availableSlots")}</Label>
                {bookingSlotsLoading ? (
                  <p style={{fontSize:13, color:'var(--text-secondary)'}}>{t("booking.loading")}</p>
                ) : bookingSlots.length === 0 ? (
                  <p style={{fontSize:13, color:'var(--text-secondary)'}}>{t("booking.noSlots")}</p>
                ) : (
                  <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
                    {bookingSlots.map((slot, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        style={{minWidth:90}}
                        onClick={() => requestBooking(slot.start_time, slot.end_time)}
                      >
                        <Clock size={14} style={{marginRight:4}} />
                        {slot.start_time} – {slot.end_time}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="field">
              <Label>Notatki <span className="optional">(opcjonalne)</span></Label>
              <Textarea
                value={bookingNotes}
                onChange={(e) => setBookingNotes(e.target.value)}
                placeholder="Np. chciałbym popracować nad plecy..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingOpen(false)}>Anuluj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Filling Dialog */}
      <Dialog open={formFillingOpen} onOpenChange={setFormFillingOpen}>
        <DialogContent className="form-filling-dialog">
          <DialogHeader>
            <DialogTitle>{fillingForm?.form_name}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="form-filling-scroll">
            <div className="dialog-form">
              {fillingForm?.fields?.map((field, i) => (
                <div key={i} className="field">
                  <Label>{field.label} {field.required && <span className="required">*</span>}</Label>
                  {field.type === 'text' && (
                    <Input value={formValues[field.name] || ""} onChange={(e) => setFormValues({...formValues, [field.name]: e.target.value})} />
                  )}
                  {field.type === 'number' && (
                    <Input type="number" value={formValues[field.name] || ""} onChange={(e) => setFormValues({...formValues, [field.name]: e.target.value})} />
                  )}
                  {field.type === 'textarea' && (
                    <Textarea value={formValues[field.name] || ""} onChange={(e) => setFormValues({...formValues, [field.name]: e.target.value})} />
                  )}
                  {field.type === 'scale' && (
                    <div className="scale-buttons">
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <button key={n} type="button" className={`scale-btn ${formValues[field.name] === n ? 'active' : ''}`} onClick={() => setFormValues({...formValues, [field.name]: n})}>
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                  {field.type === 'yesno' && (
                    <div className="yesno-buttons">
                      <Button variant={formValues[field.name] === 'yes' ? 'default' : 'outline'} onClick={() => setFormValues({...formValues, [field.name]: 'yes'})}>
                        Tak
                      </Button>
                      <Button variant={formValues[field.name] === 'no' ? 'default' : 'outline'} onClick={() => setFormValues({...formValues, [field.name]: 'no'})}>
                        Nie
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormFillingOpen(false)}>Anuluj</Button>
            <Button onClick={submitFormResponse}>Wyślij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Metric Log Dialog */}
      <Dialog open={metricLogOpen} onOpenChange={setMetricLogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedMetric?.name}</DialogTitle>
            <DialogDescription>Wprowadź dzisiejszą wartość ({selectedMetric?.unit})</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>{selectedMetric?.name} ({selectedMetric?.unit})</Label>
              <Input
                type="number"
                value={metricLogValue}
                onChange={(e) => setMetricLogValue(e.target.value)}
                placeholder={`np. ${selectedMetric?.min_value || 0}`}
                min={selectedMetric?.min_value}
                max={selectedMetric?.max_value}
                autoFocus
              />
              {selectedMetric?.min_value != null && selectedMetric?.max_value != null && (
                <p className="field-hint">Zakres: {selectedMetric.min_value} – {selectedMetric.max_value} {selectedMetric.unit}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMetricLogOpen(false)}>Anuluj</Button>
            <Button onClick={logMetricValue} disabled={!metricLogValue}>Zapisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nutrition Log Dialog */}
      <Dialog open={nutritionLogOpen} onOpenChange={setNutritionLogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dodaj posiłek</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Typ posiłku</Label>
              <Select value={newNutritionLog.meal_type} onValueChange={(v) => setNewNutritionLog({...newNutritionLog, meal_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Śniadanie</SelectItem>
                  <SelectItem value="lunch">Obiad</SelectItem>
                  <SelectItem value="dinner">Kolacja</SelectItem>
                  <SelectItem value="snack">Przekąska</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Produkty</Label>
              <IngredientSearch token={token} onPick={(picked) => {
                setNewNutritionLog((prev) => {
                  const items = [...prev.items];
                  const emptyIdx = items.findIndex((it) => !it.name || !it.name.trim());
                  if (emptyIdx >= 0) items[emptyIdx] = picked; else items.push(picked);
                  return { ...prev, items };
                });
              }} />
              {newNutritionLog.items.map((item, i) => (
                <div key={i} className="nutrition-item-row">
                  <Input placeholder="Nazwa" value={item.name} onChange={(e) => {
                    const items = [...newNutritionLog.items];
                    items[i].name = e.target.value;
                    setNewNutritionLog({...newNutritionLog, items});
                  }} />
                  <Input type="number" placeholder="kcal" className="small-input" value={item.calories || ""} onChange={(e) => {
                    const items = [...newNutritionLog.items];
                    items[i].calories = parseFloat(e.target.value) || 0;
                    setNewNutritionLog({...newNutritionLog, items});
                  }} />
                  <Input type="number" placeholder="B(g)" className="small-input" value={item.protein || ""} onChange={(e) => {
                    const items = [...newNutritionLog.items];
                    items[i].protein = parseFloat(e.target.value) || 0;
                    setNewNutritionLog({...newNutritionLog, items});
                  }} />
                  <Input type="number" placeholder="W(g)" className="small-input" value={item.carbs || ""} onChange={(e) => {
                    const items = [...newNutritionLog.items];
                    items[i].carbs = parseFloat(e.target.value) || 0;
                    setNewNutritionLog({...newNutritionLog, items});
                  }} />
                  <Input type="number" placeholder="T(g)" className="small-input" value={item.fat || ""} onChange={(e) => {
                    const items = [...newNutritionLog.items];
                    items[i].fat = parseFloat(e.target.value) || 0;
                    setNewNutritionLog({...newNutritionLog, items});
                  }} />
                  {newNutritionLog.items.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => {
                      setNewNutritionLog({...newNutritionLog, items: newNutritionLog.items.filter((_, j) => j !== i)});
                    }}><X size={12} /></Button>
                  )}
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={() => {
                setNewNutritionLog({...newNutritionLog, items: [...newNutritionLog.items, { name: "", calories: 0, protein: 0, carbs: 0, fat: 0, portion: "" }]});
              }}><Plus size={12} /> Dodaj produkt</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNutritionLogOpen(false)}>Anuluj</Button>
            <Button onClick={logNutrition} disabled={!newNutritionLog.items.some(i => i.name.trim())}>Zapisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={photoUploadOpen} onOpenChange={setPhotoUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dodaj zdjęcie postępu</DialogTitle>
            <DialogDescription>Zrób zdjęcie lub wybierz z galerii</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            {/* Camera / Gallery buttons */}
            <div style={{display:'flex', gap:8, marginBottom:12}}>
              <Button variant="outline" style={{flex:1}} onClick={async () => {
                const photo = await CameraService.takePhoto(newPhoto.photo_type);
                if (photo) setNewPhoto(p => ({...p, photo_url: photo.dataUrl, _capturedPhoto: photo}));
              }}>
                <Camera size={16} style={{marginRight:6}} /> Aparat
              </Button>
              <Button variant="outline" style={{flex:1}} onClick={async () => {
                const photo = await CameraService.pickFromGallery();
                if (photo) setNewPhoto(p => ({...p, photo_url: photo.dataUrl, _capturedPhoto: photo}));
              }}>
                <Image size={16} style={{marginRight:6}} /> Galeria
              </Button>
            </div>
            {/* Preview */}
            {newPhoto.photo_url && (
              <div style={{textAlign:'center', marginBottom:12}}>
                <img src={newPhoto.photo_url} alt="Preview" style={{maxWidth:'100%', maxHeight:200, borderRadius:8}} />
              </div>
            )}
            {/* Fallback URL input */}
            <div className="field">
              <Label>Lub wklej URL</Label>
              <Input
                value={newPhoto.photo_url?.startsWith('data:') ? '' : newPhoto.photo_url}
                onChange={(e) => setNewPhoto({...newPhoto, photo_url: e.target.value, _capturedPhoto: null})}
                placeholder="https://..."
                data-testid="photo-url-input"
              />
            </div>
            <div className="field">
              <Label>Typ zdjęcia</Label>
              <Select value={newPhoto.photo_type} onValueChange={(v) => setNewPhoto({...newPhoto, photo_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="front">Przód</SelectItem>
                  <SelectItem value="side">Bok</SelectItem>
                  <SelectItem value="back">Tył</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Notatki <span className="optional">({t("general.optional")})</span></Label>
              <Textarea
                value={newPhoto.notes}
                onChange={(e) => setNewPhoto({...newPhoto, notes: e.target.value})}
                placeholder="Np. po 4 tygodniach diety..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoUploadOpen(false)}>{t("action.cancel")}</Button>
            <Button onClick={uploadProgressPhoto} data-testid="submit-photo">{t("action.add")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Technique Video Upload Dialog */}
      <Dialog open={techniqueUploadOpen} onOpenChange={setTechniqueUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wyślij video do oceny</DialogTitle>
            <DialogDescription>Trener sprawdzi Twoją technikę</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            {/* Video file picker */}
            <div style={{display:'flex', gap:8, marginBottom:8}}>
              <Button variant="outline" style={{flex:1}} onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'video/*';
                input.onchange = (e) => {
                  const f = e.target.files?.[0];
                  if (f) setNewTechniqueVideo(v => ({...v, _file: f, video_url: ""}));
                };
                input.click();
              }}>
                <Video size={16} style={{marginRight:6}} /> Wybierz plik video
              </Button>
            </div>
            {newTechniqueVideo._file && (
              <div style={{fontSize:13, color:'var(--text-secondary)', marginBottom:8}}>
                Wybrano: {newTechniqueVideo._file.name}
              </div>
            )}
            <div className="field">
              <Label>Lub wklej URL video</Label>
              <Input
                value={newTechniqueVideo.video_url}
                onChange={(e) => setNewTechniqueVideo({...newTechniqueVideo, video_url: e.target.value, _file: null})}
                placeholder="https://..."
                data-testid="video-url-input"
              />
            </div>
            <div className="field">
              <Label>Nazwa ćwiczenia</Label>
              <Input
                value={newTechniqueVideo.exercise_name}
                onChange={(e) => setNewTechniqueVideo({...newTechniqueVideo, exercise_name: e.target.value})}
                placeholder="np. Przysiad ze sztangą"
                data-testid="exercise-name-input"
              />
            </div>
            <div className="field">
              <Label>Pytanie do trenera <span className="optional">(opcjonalne)</span></Label>
              <Textarea
                value={newTechniqueVideo.notes}
                onChange={(e) => setNewTechniqueVideo({...newTechniqueVideo, notes: e.target.value})}
                placeholder="Czy moja technika jest OK?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTechniqueUploadOpen(false)}>Anuluj</Button>
            <Button onClick={uploadTechniqueVideo} data-testid="submit-video">Wyślij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications Dialog */}
      <Dialog open={clientNotifOpen} onOpenChange={setClientNotifOpen}>
        <DialogContent className="notif-dialog">
          <DialogHeader>
            <DialogTitle>Powiadomienia</DialogTitle>
          </DialogHeader>
          <ScrollArea className="notif-scroll">
            {clientNotifications.length === 0 ? (
              <p className="notif-empty">Brak powiadomień</p>
            ) : (
              <div className="notif-list">
                {clientNotifications.map(n => (
                  <div key={n.id} className={`notif-item ${n.read ? '' : 'unread'}`}>
                    <div className="notif-icon">
                      {n.type === 'automation' && <Zap size={16} />}
                      {n.type === 'workout' && <Dumbbell size={16} />}
                      {n.type === 'form' && <ClipboardList size={16} />}
                      {n.type === 'session' && <Calendar size={16} />}
                      {n.type === 'message' && <MessageCircle size={16} />}
                      {n.type === 'ticket' && <LifeBuoy size={16} />}
                      {!['automation', 'workout', 'form', 'session', 'message', 'ticket'].includes(n.type) && <Bell size={16} />}
                    </div>
                    <div className="notif-content">
                      <p className="notif-title">{n.title}</p>
                      <p className="notif-message">{n.message}</p>
                      <span className="notif-time">{format(new Date(n.created_at), "d MMM, HH:mm")}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* AI COACH DIALOG */}
      <Dialog open={aiCoachOpen} onOpenChange={setAiCoachOpen}>
        <DialogContent className="dialog-content" style={{maxWidth:540}}>
          <DialogHeader>
            <DialogTitle><Zap size={20} /> AI Coach — Generuj plan biegowy</DialogTitle>
            <DialogDescription>AI wygeneruje spersonalizowany plan interwałowy na podstawie Twojej historii biegów.</DialogDescription>
          </DialogHeader>

          {!aiGeneratedPlan ? (
            <div style={{display:'flex', flexDirection:'column', gap:12}}>
              <div>
                <Label>Cel treningu</Label>
                <Select value={aiCoachForm.goal} onValueChange={v => setAiCoachForm(p => ({...p, goal: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general_fitness">Ogolna kondycja</SelectItem>
                    <SelectItem value="tempo">Poprawa tempa</SelectItem>
                    <SelectItem value="endurance">Wytrzymalosc</SelectItem>
                    <SelectItem value="weight_loss">Odchudzanie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Trudnosc</Label>
                <Select value={aiCoachForm.difficulty} onValueChange={v => setAiCoachForm(p => ({...p, difficulty: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Latwy</SelectItem>
                    <SelectItem value="medium">Sredni</SelectItem>
                    <SelectItem value="hard">Trudny</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Czas treningu (min)</Label>
                <Input type="number" value={aiCoachForm.duration_minutes} onChange={e => setAiCoachForm(p => ({...p, duration_minutes: parseInt(e.target.value) || 20}))} min={10} max={120} />
              </div>
              <Button onClick={handleGenerateAiPlan} disabled={aiCoachLoading}>
                {aiCoachLoading ? <><Clock size={16} className="animate-spin" /> Generowanie...</> : <><Zap size={16} /> Generuj plan</>}
              </Button>
            </div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:12}}>
              <Card>
                <CardHeader style={{paddingBottom:8}}>
                  <CardTitle style={{fontSize:16}}>{aiGeneratedPlan.name}</CardTitle>
                  {aiGeneratedPlan.description && <CardDescription>{aiGeneratedPlan.description}</CardDescription>}
                </CardHeader>
                <CardContent>
                  <div style={{display:'flex', gap:12, flexWrap:'wrap', marginBottom:12}}>
                    <Badge variant="outline"><Clock size={12} /> {aiGeneratedPlan.estimated_duration_minutes} min</Badge>
                    <Badge variant="outline"><RouteIcon size={12} /> {aiGeneratedPlan.estimated_distance_km?.toFixed(1)} km</Badge>
                    <Badge>{difficultyLabels[aiGeneratedPlan.difficulty] || aiGeneratedPlan.difficulty}</Badge>
                  </div>
                  <p style={{fontSize:13, fontWeight:600, marginBottom:6}}>Interwaly ({aiGeneratedPlan.intervals?.length})</p>
                  <div style={{display:'flex', flexDirection:'column', gap:4, maxHeight:200, overflow:'auto'}}>
                    {aiGeneratedPlan.intervals?.map((iv, i) => (
                      <div key={i} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 8px', borderRadius:6, background:'var(--bg-secondary)', fontSize:13}}>
                        <span style={{fontWeight:500}}>{iv.order}. {iv.type}</span>
                        <span>{Math.floor(iv.duration_seconds / 60)}:{String(iv.duration_seconds % 60).padStart(2,'0')}</span>
                        <span style={{color:'var(--text-secondary)', fontSize:12}}>{iv.description}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {aiCoachNotes && (
                <Card style={{borderColor:'var(--primary)'}}>
                  <CardContent style={{padding:12}}>
                    <p style={{fontSize:13, fontWeight:600, marginBottom:4}}><Star size={14} /> Wskazowki AI</p>
                    <p style={{fontSize:13, color:'var(--text-secondary)'}}>{aiCoachNotes}</p>
                  </CardContent>
                </Card>
              )}

              <div style={{display:'flex', gap:8}}>
                <Button style={{flex:1}} onClick={handleAcceptAiPlan}><Check size={16} /> Zapisz plan</Button>
                <Button variant="outline" style={{flex:1}} onClick={() => setAiGeneratedPlan(null)}><Edit size={16} /> Generuj ponownie</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI PROGRESS DIALOG */}
      <Dialog open={aiProgressOpen} onOpenChange={setAiProgressOpen}>
        <DialogContent className="dialog-content" style={{maxWidth:500}}>
          <DialogHeader>
            <DialogTitle><TrendingUp size={20} /> AI Analiza postepoow</DialogTitle>
            <DialogDescription>AI analizuje Twoje treningi biegowe i daje rekomendacje.</DialogDescription>
          </DialogHeader>
          {aiProgressLoading ? (
            <div style={{display:'flex', alignItems:'center', gap:8, padding:20, justifyContent:'center'}}>
              <Clock size={20} className="animate-spin" />
              <span>Analizowanie...</span>
            </div>
          ) : (
            <div style={{whiteSpace:'pre-wrap', fontSize:14, lineHeight:1.6}}>
              {aiProgressText}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Build your own workout plan (solo client) */}
      <Dialog open={planBuilderOpen} onOpenChange={(v) => { setPlanBuilderOpen(v); if (!v) setEditingPlanId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPlanId ? "Edytuj plan" : "Stwórz własny plan"}</DialogTitle>
            <DialogDescription>Nazwij plan, dodaj ćwiczenia i ustaw serie/powtórzenia/ciężar dla każdego.</DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Input placeholder="Nazwa planu (np. Mój trening A)" value={planName} onChange={(e) => setPlanName(e.target.value)} />

            {/* Wybrane ćwiczenia — osobno od przeglądania katalogu, żeby usunięcie
                nie wymagało przewijania 2000+ pozycji szukając tego samego. */}
            {planSel.length > 0 && (
              <ScrollArea style={{ maxHeight: "28vh" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {planSel.map((s) => (
                    <div key={s.exercise_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 8, background: "var(--glass-bg)" }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                      <Input type="number" min="1" value={s.sets} onChange={(e) => updatePlanExercise(s.exercise_id, "sets", e.target.value)}
                             title="Serie" style={{ width: 48, height: 32, padding: "2px 4px", textAlign: "center" }} />
                      <span style={{ fontSize: 11, opacity: 0.6 }}>×</span>
                      <Input type="number" min="1" value={s.reps} onChange={(e) => updatePlanExercise(s.exercise_id, "reps", e.target.value)}
                             title="Powtórzenia" style={{ width: 48, height: 32, padding: "2px 4px", textAlign: "center" }} />
                      <Input type="number" min="0" step="0.5" value={s.weight} onChange={(e) => updatePlanExercise(s.exercise_id, "weight", e.target.value)}
                             title="Ciężar (kg)" style={{ width: 56, height: 32, padding: "2px 4px", textAlign: "center" }} />
                      <button type="button" onClick={() => removeExerciseFromPlan(s.exercise_id)} aria-label="Usuń ćwiczenie"
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 4, flexShrink: 0 }}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

            <div style={{ position: "relative" }}>
              <Search size={16} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text-secondary)" }} />
              <Input placeholder="Szukaj ćwiczenia do dodania..." value={planExerciseSearch}
                     onChange={(e) => setPlanExerciseSearch(e.target.value)} style={{ paddingLeft: 32 }} />
            </div>
            <ScrollArea className="exercise-picker-list" style={{ maxHeight: "26vh" }}>
              {availableExercises
                .filter((ex) => !planExerciseSearch || ex.name?.toLowerCase().includes(planExerciseSearch.toLowerCase()))
                .slice(0, 100)
                .map((ex) => {
                const on = planSel.some((s) => s.exercise_id === ex.id);
                return (
                  <div key={ex.id} className="exercise-picker-item" onClick={() => on ? removeExerciseFromPlan(ex.id) : addExerciseToPlan(ex)}
                    style={{ background: on ? "var(--glass-bg)" : undefined }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      {ex.image_url && <img src={ex.image_url} alt={ex.name} loading="lazy" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: "#fff" }} />}
                      <div className="exercise-picker-info">
                        <span className="exercise-picker-name">{ex.name}</span>
                        <span className="exercise-picker-category">{ex.category}</span>
                      </div>
                    </div>
                    {on ? <Check size={18} color="#22c55e" /> : <Plus size={18} />}
                  </div>
                );
              })}
            </ScrollArea>
            <Button onClick={saveMyPlan} disabled={savingPlan}>
              {savingPlan ? "Zapisuję..." : editingPlanId ? "Zapisz zmiany" : "Utwórz plan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <HelpButton />
    </div>
  );
};


export default ClientDashboard;
